import React, { useState, useEffect, useCallback } from 'react';
import { 
  Award, Send, Users, Heart, Lightbulb, Star, Target, Rocket, Vote, Plus, 
  Trophy, Sparkles, AlertCircle, Check, X, TrendingUp,
  MessageSquare, Crown, Zap, PartyPopper, Clock,
  Gamepad2, Brain, Timer, Play, RotateCcw, Swords, Building2, Quote,
  Hand, Hash, ArrowRight, ChevronRight, Crosshair, Dices, Eye, RefreshCw,
  CheckSquare, CalendarDays, Megaphone, MessageSquarePlus, ClipboardList, Grid3x3, Coins
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { JewelModal, JewelInput, JewelTextarea, JewelSelect, JewelButton, JewelField, JewelChipRow } from '../../components/ui/jewel-modal';
import { toast } from 'sonner';
import axios from 'axios';
import TasksPage from './TasksPage';
import MeetingsPage from './MeetingsPage';
import AnnouncementsPage from './AnnouncementsPage';
import PollsPage from './PollsPage';
import FeedbackPage from './FeedbackPage';
import GamificationPage from './GamificationPage';
import HRPage from './HRPage';
import TodayGlanceWidget from '../../components/TodayGlanceWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KUDOS_CATEGORIES = {
  'teamwork': { icon: Users, label: 'Teamwork', gradient: 'from-blue-500 to-cyan-500', bg: 'bg-blue-500', emoji: '🤝' },
  'innovation': { icon: Lightbulb, label: 'Innovation', gradient: 'from-amber-500 to-orange-500', bg: 'bg-amber-500', emoji: '💡' },
  'customer-service': { icon: Star, label: 'Service', gradient: 'from-purple-500 to-pink-500', bg: 'bg-purple-500', emoji: '⭐' },
  'leadership': { icon: Target, label: 'Leadership', gradient: 'from-emerald-500 to-teal-500', bg: 'bg-emerald-500', emoji: '🎯' },
  'going-above': { icon: Rocket, label: 'Above & Beyond', gradient: 'from-rose-500 to-red-500', bg: 'bg-rose-500', emoji: '🚀' }
};

const SOLO_GAMES = [
  { id: 'memory', name: 'Memory Match', icon: Brain, gradient: 'from-purple-500 to-pink-500', desc: 'Find matching pairs', color: 'purple' },
  { id: 'trivia', name: 'Quick Trivia', icon: Lightbulb, gradient: 'from-amber-500 to-orange-500', desc: 'Test your knowledge', color: 'amber' },
  { id: 'wordle', name: 'Word Guess', icon: MessageSquare, gradient: 'from-green-500 to-emerald-500', desc: '5-letter challenge', color: 'green' },
  { id: 'reaction', name: 'Reflex Test', icon: Zap, gradient: 'from-yellow-400 to-amber-500', desc: 'How fast are you?', color: 'yellow' },
  { id: 'typing', name: 'Speed Typer', icon: Timer, gradient: 'from-cyan-500 to-blue-500', desc: 'WPM challenge', color: 'cyan' },
  { id: 'number-guess', name: 'Number Guess', icon: Hash, gradient: 'from-rose-500 to-red-500', desc: 'Guess 1-100', color: 'rose' },
  { id: 'emoji-quiz', name: 'Emoji Quiz', icon: Eye, gradient: 'from-violet-500 to-purple-500', desc: 'Decode the emojis', color: 'violet' },
  { id: 'color-match', name: 'Color Match', icon: Crosshair, gradient: 'from-teal-500 to-emerald-500', desc: 'Test your eye', color: 'teal' },
  { id: '2048', name: '2048', icon: Grid3x3, gradient: 'from-orange-500 to-amber-600', desc: 'Merge tiles to 2048', color: 'orange' },
  { id: 'coin-streak', name: 'Coin Streak', icon: Coins, gradient: 'from-yellow-500 to-orange-500', desc: 'Predict heads or tails', color: 'yellow' },
];

const MULTIPLAYER_GAMES = [
  { id: 'rps', name: 'Rock Paper Scissors', icon: Swords, gradient: 'from-red-500 to-rose-500', desc: 'Classic showdown', color: 'red' },
  { id: 'trivia-battle', name: 'Trivia Battle', icon: Trophy, gradient: 'from-indigo-500 to-purple-500', desc: '5-question duel', color: 'indigo' },
  { id: 'word-duel', name: 'Word Duel', icon: MessageSquare, gradient: 'from-emerald-500 to-teal-500', desc: 'Same word, who guesses first?', color: 'emerald' },
];

const MEMORY_EMOJIS = ['🎯', '🚀', '💡', '⭐', '🔥', '💎', '🎨', '🎮'];
const TRIVIA_QS = [
  { q: 'What does "WFH" stand for?', opts: ['Work From Home', 'Work For Hours', 'Weekly Free Hours', 'Work First Half'], ans: 0 },
  { q: 'What is a "standup" meeting?', opts: ['Exercise', 'Daily sync', 'Standing desk', 'Promotion'], ans: 1 },
  { q: 'What does "EOD" mean?', opts: ['End of Day', 'Every Other Day', 'Employee of Decade', 'End of Doc'], ans: 0 },
  { q: 'What does "KPI" stand for?', opts: ['Key Personal Index', 'Key Performance Indicator', 'Knowledge Protocol Info', 'Keep Producing Ideas'], ans: 1 },
  { q: 'What is "onboarding"?', opts: ['Surfing lesson', 'New hire integration', 'Board meeting', 'Ship departure'], ans: 1 },
];
const WORDLE_WORDS = ['TEAMS', 'SLACK', 'EMAIL', 'TASKS', 'GOALS', 'FOCUS', 'DRAFT'];
const TYPING_TEXTS = [
  'The quick brown fox jumps over the lazy dog.',
  'Teamwork makes the dream work.',
  'Customer satisfaction is our top priority.',
  'Every shift starts with a positive attitude.',
];
const EMOJI_QUIZ_DATA = [
  { emojis: '🏠 + 🔑', answer: 'HOUSE KEY', hint: 'You need this to get in' },
  { emojis: '☕ + ⏰', answer: 'COFFEE BREAK', hint: 'Mid-shift essential' },
  { emojis: '📧 + 📤', answer: 'SEND EMAIL', hint: 'Daily work task' },
  { emojis: '👔 + 🏢', answer: 'OFFICE WORK', hint: 'Where you work' },
  { emojis: '🤝 + 👥', answer: 'TEAMWORK', hint: 'Together we achieve' },
  { emojis: '📅 + ✈️', answer: 'HOLIDAY', hint: 'Time off' },
  { emojis: '💰 + 📆', answer: 'PAYDAY', hint: 'Everyone\'s favorite day' },
  { emojis: '🎯 + 📊', answer: 'TARGETS', hint: 'Goals to hit' },
];
const COLOR_NAMES = ['Red', 'Blue', 'Green', 'Yellow', 'Purple', 'Orange', 'Pink', 'Cyan'];
const COLOR_HEX = { Red: '#ef4444', Blue: '#3b82f6', Green: '#22c55e', Yellow: '#eab308', Purple: '#a855f7', Orange: '#f97316', Pink: '#ec4899', Cyan: '#06b6d4' };

const StaffHubPageRevamped = ({ getAuthHeaders, currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [kudosFeed, setKudosFeed] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [polls, setPolls] = useState([]);
  const [stats, setStats] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [pendingKudos, setPendingKudos] = useState([]);
  const [pendingPolls, setPendingPolls] = useState([]);
  
  const [showKudos, setShowKudos] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [showApprovals, setShowApprovals] = useState(false);
  const [showGame, setShowGame] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMultiplayerGame, setShowMultiplayerGame] = useState(false);
  
  const [kudosForm, setKudosForm] = useState({ recipient_username: '', message: '', category: 'teamwork' });
  const [pollForm, setPollForm] = useState({ question: '', options: ['', ''] });
  
  const [game, setGame] = useState(null);
  const [scores, setScores] = useState({});
  const [memoryCards, setMemoryCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [triviaQ, setTriviaQ] = useState(null);
  const [triviaScore, setTriviaScore] = useState(0);
  const [triviaNum, setTriviaNum] = useState(0);
  const [wordleWord, setWordleWord] = useState('');
  const [wordleGuesses, setWordleGuesses] = useState([]);
  const [wordleInput, setWordleInput] = useState('');
  const [reactionStart, setReactionStart] = useState(null);
  const [reactionResult, setReactionResult] = useState(null);
  const [reactionWaiting, setReactionWaiting] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [typingInput, setTypingInput] = useState('');
  const [typingStart, setTypingStart] = useState(null);
  const [typingWPM, setTypingWPM] = useState(null);
  
  // New solo game states
  const [numberTarget, setNumberTarget] = useState(0);
  const [numberGuess, setNumberGuess] = useState('');
  const [numberAttempts, setNumberAttempts] = useState([]);
  const [numberWon, setNumberWon] = useState(false);
  const [emojiQuiz, setEmojiQuiz] = useState(null);
  const [emojiAnswer, setEmojiAnswer] = useState('');
  const [emojiScore, setEmojiScore] = useState(0);
  const [emojiNum, setEmojiNum] = useState(0);
  const [colorTarget, setColorTarget] = useState('');
  const [colorOptions, setColorOptions] = useState([]);
  const [colorScore, setColorScore] = useState(0);
  const [colorRound, setColorRound] = useState(0);
  const [colorTotalRounds] = useState(10);

  // 2048 state
  const [g2048, setG2048] = useState([[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0]]);
  const [g2048Score, setG2048Score] = useState(0);
  const [g2048Over, setG2048Over] = useState(false);
  const [g2048Won, setG2048Won] = useState(false);

  // Coin Streak state
  const [coinFace, setCoinFace] = useState(null); // 'H' | 'T'
  const [coinStreak, setCoinStreak] = useState(0);
  const [coinBest, setCoinBest] = useState(0);
  const [coinFlipping, setCoinFlipping] = useState(false);
  const [coinHistory, setCoinHistory] = useState([]); // last 10 faces
  
  // Multiplayer states
  const [gameInvites, setGameInvites] = useState([]);
  const [gameHistory, setGameHistory] = useState([]);
  const [selectedMultiplayerGame, setSelectedMultiplayerGame] = useState(null);
  const [inviteTarget, setInviteTarget] = useState('');
  const [activeMultiplayerGame, setActiveMultiplayerGame] = useState(null);
  const [mpGameState, setMpGameState] = useState(null);
  const [rpsChoice, setRpsChoice] = useState(null);
  const [wordDuelInput, setWordDuelInput] = useState('');
  const [gamesSubTab, setGamesSubTab] = useState('solo');

  // Dashboard widget data
  const [tasks, setTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [feedbackList, setFeedbackList] = useState([]);
  const [gamPoints, setGamPoints] = useState({ points: 0, history: [] });
  const [gamBadges, setGamBadges] = useState({ all_badges: [], earned: [] });
  const [sotm, setSotm] = useState({ current: {} });
  const [expandedSection, setExpandedSection] = useState(null);
  const [showGamesSection, setShowGamesSection] = useState(false);
  const [hubTab, setHubTab] = useState('overview');
  const [hrInitialTab, setHrInitialTab] = useState('expenses');

  const isExec = ['superadmin', 'executive', 'admin'].includes(currentUser?.role);
  const canManage = isExec || currentUser?.role === 'manager';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const h = getAuthHeaders();
      if (!h) return;
      const [f, b, p, s, st] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/staff-hub/kudos/feed`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/staff-hub/kudos/leaderboard`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/staff-hub/polls`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/staff-hub/stats`, { headers: h }),
        axios.get(`${BACKEND_URL}/api/admin/staff-profiles`, { headers: h })
      ]);
      setKudosFeed(f.data); setLeaderboard(b.data); setPolls(p.data); setStats(s.data);
      setStaffList(st.data.filter(x => x.username !== currentUser?.username));
      if (isExec) {
        const [pk, pp] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/staff-hub/kudos/pending`, { headers: h }),
          axios.get(`${BACKEND_URL}/api/staff-hub/polls/pending`, { headers: h })
        ]);
        setPendingKudos(pk.data); setPendingPolls(pp.data);
      }
      // Fetch game data
      try {
        const [inv, hist] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/games/invites`, { headers: h }),
          axios.get(`${BACKEND_URL}/api/games/history`, { headers: h })
        ]);
        setGameInvites(inv.data);
        setGameHistory(hist.data);
      } catch (e) { console.error('Games fetch error:', e); }
      // Fetch dashboard widget data
      try {
        const [tk, mt, an, fb, gp, gb, sm] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/productivity/tasks`, { headers: h }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/productivity/meetings`, { headers: h }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/engagement/announcements`, { headers: h }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/engagement/feedback`, { headers: h }).catch(() => ({ data: [] })),
          axios.get(`${BACKEND_URL}/api/gamification/points`, { headers: h }).catch(() => ({ data: { points: 0, history: [] } })),
          axios.get(`${BACKEND_URL}/api/gamification/badges`, { headers: h }).catch(() => ({ data: { all_badges: [], earned: [] } })),
          axios.get(`${BACKEND_URL}/api/gamification/staff-of-month`, { headers: h }).catch(() => ({ data: { current: {} } }))
        ]);
        setTasks(tk.data); setMeetings(mt.data); setAnnouncements(an.data);
        setFeedbackList(fb.data); setGamPoints(gp.data); setGamBadges(gb.data); setSotm(sm.data);
      } catch (e) { console.error('Widget fetch error:', e); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const sendKudos = async () => {
    if (!kudosForm.recipient_username || !kudosForm.message.trim()) { toast.error('Fill all fields'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/staff-hub/kudos`, kudosForm, { headers: getAuthHeaders() });
      toast.success('Kudos sent!'); setShowKudos(false);
      setKudosForm({ recipient_username: '', message: '', category: 'teamwork' }); fetchData();
    } catch { toast.error('Failed'); }
  };

  const createPoll = async () => {
    if (!pollForm.question.trim() || pollForm.options.filter(o => o.trim()).length < 2) { toast.error('Need question + 2 options'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/staff-hub/polls`, { ...pollForm, options: pollForm.options.filter(o => o.trim()) }, { headers: getAuthHeaders() });
      toast.success('Poll created!'); setShowPoll(false);
      setPollForm({ question: '', options: ['', ''] }); fetchData();
    } catch { toast.error('Failed'); }
  };

  const vote = async (id, idx) => {
    try {
      await axios.post(`${BACKEND_URL}/api/staff-hub/polls/${id}/vote`, { option_index: idx }, { headers: getAuthHeaders() });
      toast.success('Voted!'); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const approve = async (type, id, ok) => {
    try {
      const url = type === 'kudos' ? `${BACKEND_URL}/api/staff-hub/kudos/${id}/approve?approved=${ok}` : `${BACKEND_URL}/api/staff-hub/polls/${id}/approve?approved=${ok}`;
      await axios.put(url, {}, { headers: getAuthHeaders() });
      toast.success(ok ? 'Approved!' : 'Rejected'); fetchData();
    } catch { toast.error('Failed'); }
  };

  // Games — persist best scores across sessions
  useEffect(() => {
    try { const s = JSON.parse(localStorage.getItem('hub_game_scores') || '{}'); if (s && typeof s === 'object') setScores(s); } catch {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('hub_game_scores', JSON.stringify(scores)); } catch {}
  }, [scores]);

  // Games — DON'T reset progress when re-opening the same game.
  // Only init when switching game OR the previous run finished.
  const openGame = (g) => {
    setShowGame(true);
    const same = game?.id === g.id;
    if (same) { setGame(g); return; } // keep current state
    setGame(g);
    if (g.id === 'memory') initMem();
    if (g.id === 'trivia') initTriv();
    if (g.id === 'wordle') initWord();
    if (g.id === 'typing') initType();
    if (g.id === 'reaction') { setReactionStart(null); setReactionResult(null); setReactionWaiting(false); }
    if (g.id === 'number-guess') initNumberGuess();
    if (g.id === 'emoji-quiz') initEmojiQuiz();
    if (g.id === 'color-match') initColorMatch();
    if (g.id === '2048') init2048();
    if (g.id === 'coin-streak') initCoinStreak();
  };

  const initMem = () => {
    const c = [...MEMORY_EMOJIS, ...MEMORY_EMOJIS].sort(() => Math.random() - 0.5).map((e, i) => ({ id: i, emoji: e, matched: false }));
    setMemoryCards(c); setFlipped([]); setMatched([]); setMoves(0);
  };
  const clickMem = (card) => {
    if (flipped.length === 2 || card.matched || flipped.includes(card.id)) return;
    const nf = [...flipped, card.id]; setFlipped(nf);
    if (nf.length === 2) {
      setMoves(m => m + 1);
      const [a, b] = nf;
      const c1 = memoryCards.find(c => c.id === a), c2 = memoryCards.find(c => c.id === b);
      if (c1.emoji === c2.emoji) {
        setMatched(p => [...p, c1.emoji]);
        setMemoryCards(cs => cs.map(c => c.id === a || c.id === b ? { ...c, matched: true } : c));
        setFlipped([]);
        if (matched.length + 1 === MEMORY_EMOJIS.length) { toast.success(`Done! ${moves + 1} moves`); setScores(s => ({ ...s, memory: Math.min(s.memory || 999, moves + 1) })); }
      } else setTimeout(() => setFlipped([]), 800);
    }
  };

  const initTriv = () => { setTriviaScore(0); setTriviaNum(0); setTriviaQ(TRIVIA_QS[0]); };
  const ansTriv = (i) => {
    if (i === triviaQ.ans) { setTriviaScore(s => s + 1); toast.success('Correct!'); } else toast.error('Wrong!');
    if (triviaNum + 1 < TRIVIA_QS.length) { setTriviaNum(n => n + 1); setTriviaQ(TRIVIA_QS[triviaNum + 1]); }
    else { toast.success(`Score: ${i === triviaQ.ans ? triviaScore + 1 : triviaScore}/${TRIVIA_QS.length}`); setTriviaQ(null); }
  };

  const initWord = () => { setWordleWord(WORDLE_WORDS[Math.floor(Math.random() * WORDLE_WORDS.length)]); setWordleGuesses([]); setWordleInput(''); };
  const guessWord = () => {
    if (wordleInput.length !== 5) return;
    const g = wordleInput.toUpperCase(); setWordleGuesses(gs => [...gs, g]); setWordleInput('');
    if (g === wordleWord) toast.success(`Got it in ${wordleGuesses.length + 1}!`);
    else if (wordleGuesses.length >= 5) toast.error(`Word: ${wordleWord}`);
  };
  const wordColor = (l, i) => wordleWord[i] === l ? 'bg-green-500' : wordleWord.includes(l) ? 'bg-yellow-500' : 'bg-slate-600';

  const startReact = () => { setReactionResult(null); setReactionWaiting(true); setTimeout(() => { setReactionStart(Date.now()); setReactionWaiting(false); }, Math.random() * 3000 + 1500); };
  const clickReact = () => {
    if (reactionWaiting) { setReactionResult('Too early!'); setReactionWaiting(false); return; }
    if (reactionStart) { const t = Date.now() - reactionStart; setReactionResult(`${t}ms`); setReactionStart(null); setScores(s => ({ ...s, reaction: Math.min(s.reaction || 9999, t) })); }
  };

  const initType = () => { setTypingText(TYPING_TEXTS[Math.floor(Math.random() * TYPING_TEXTS.length)]); setTypingInput(''); setTypingStart(null); setTypingWPM(null); };
  const onType = (v) => {
    if (!typingStart && v.length === 1) setTypingStart(Date.now());
    setTypingInput(v);
    if (v === typingText) { const wpm = Math.round(typingText.split(' ').length / ((Date.now() - typingStart) / 60000)); setTypingWPM(wpm); toast.success(`${wpm} WPM!`); setScores(s => ({ ...s, typing: Math.max(s.typing || 0, wpm) })); }
  };

  // New games
  const initNumberGuess = () => { setNumberTarget(Math.floor(Math.random() * 100) + 1); setNumberGuess(''); setNumberAttempts([]); setNumberWon(false); };
  const guessNumber = () => {
    const n = parseInt(numberGuess);
    if (isNaN(n) || n < 1 || n > 100) return;
    const hint = n === numberTarget ? 'correct' : n < numberTarget ? 'higher' : 'lower';
    const newAttempts = [...numberAttempts, { guess: n, hint }];
    setNumberAttempts(newAttempts);
    setNumberGuess('');
    if (hint === 'correct') { setNumberWon(true); toast.success(`Got it in ${newAttempts.length} tries!`); setScores(s => ({ ...s, 'number-guess': Math.min(s['number-guess'] || 99, newAttempts.length) })); }
  };

  const initEmojiQuiz = () => { setEmojiScore(0); setEmojiNum(0); setEmojiQuiz(EMOJI_QUIZ_DATA[0]); setEmojiAnswer(''); };
  const checkEmoji = () => {
    if (!emojiAnswer.trim()) return;
    const correct = emojiAnswer.trim().toUpperCase() === emojiQuiz.answer;
    if (correct) { setEmojiScore(s => s + 1); toast.success('Correct!'); } else { toast.error(`It was: ${emojiQuiz.answer}`); }
    setEmojiAnswer('');
    if (emojiNum + 1 < EMOJI_QUIZ_DATA.length) { setEmojiNum(n => n + 1); setEmojiQuiz(EMOJI_QUIZ_DATA[emojiNum + 1]); }
    else { setEmojiQuiz(null); toast.success(`Final: ${correct ? emojiScore + 1 : emojiScore}/${EMOJI_QUIZ_DATA.length}`); }
  };

  const initColorMatch = () => {
    setColorScore(0); setColorRound(0);
    generateColorRound();
  };
  const generateColorRound = () => {
    const target = COLOR_NAMES[Math.floor(Math.random() * COLOR_NAMES.length)];
    setColorTarget(target);
    const shuffled = [...COLOR_NAMES].sort(() => Math.random() - 0.5).slice(0, 4);
    if (!shuffled.includes(target)) shuffled[Math.floor(Math.random() * 4)] = target;
    setColorOptions(shuffled);
  };
  const pickColor = (color) => {
    if (color === colorTarget) { setColorScore(s => s + 1); toast.success('Correct!'); } else { toast.error('Wrong!'); }
    if (colorRound + 1 < colorTotalRounds) {
      setColorRound(r => r + 1);
      const newTarget = COLOR_NAMES[Math.floor(Math.random() * COLOR_NAMES.length)];
      setColorTarget(newTarget);
      const shuffled = [...COLOR_NAMES].sort(() => Math.random() - 0.5).slice(0, 4);
      if (!shuffled.includes(newTarget)) shuffled[Math.floor(Math.random() * 4)] = newTarget;
      setColorOptions(shuffled);
    } else { toast.success(`Score: ${color === colorTarget ? colorScore + 1 : colorScore}/${colorTotalRounds}`); setColorTarget(''); }
  };

  // ═══ 2048 ═══
  const g2048AddTile = (grid) => {
    const empties = [];
    for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) if (grid[r][c] === 0) empties.push([r, c]);
    if (empties.length === 0) return grid;
    const [r, c] = empties[Math.floor(Math.random() * empties.length)];
    grid[r][c] = Math.random() < 0.9 ? 2 : 4;
    return grid;
  };
  const init2048 = () => {
    let g = [[0,0,0,0],[0,0,0,0],[0,0,0,0],[0,0,0,0]];
    g = g2048AddTile(g); g = g2048AddTile(g);
    setG2048(g); setG2048Score(0); setG2048Over(false); setG2048Won(false);
  };
  const g2048Slide = (row) => {
    let arr = row.filter(v => v !== 0);
    let gained = 0;
    for (let i = 0; i < arr.length - 1; i++) {
      if (arr[i] === arr[i + 1]) { arr[i] *= 2; gained += arr[i]; arr[i + 1] = 0; }
    }
    arr = arr.filter(v => v !== 0);
    while (arr.length < 4) arr.push(0);
    return { row: arr, gained };
  };
  const g2048Move = (dir) => {
    if (g2048Over) return;
    const grid = g2048.map(r => [...r]);
    let gained = 0;
    let moved = false;
    const rotate = (g) => g[0].map((_, c) => g.map(r => r[c])).reverse();
    let working = grid;
    let rotations = { left: 0, up: 1, right: 2, down: 3 }[dir] || 0;
    for (let i = 0; i < rotations; i++) working = rotate(working);
    const before = JSON.stringify(working);
    working = working.map(row => { const { row: nr, gained: ng } = g2048Slide(row); gained += ng; return nr; });
    if (JSON.stringify(working) !== before) moved = true;
    for (let i = 0; i < (4 - rotations) % 4; i++) working = rotate(working);
    if (!moved) return;
    const newGrid = g2048AddTile(working);
    setG2048(newGrid);
    setG2048Score(s => {
      const ns = s + gained;
      setScores(sc => ({ ...sc, '2048': Math.max(sc['2048'] || 0, ns) }));
      return ns;
    });
    // Check win
    if (!g2048Won && newGrid.some(r => r.some(v => v >= 2048))) { setG2048Won(true); toast.success('You reached 2048! Keep going for a higher score.'); }
    // Check game over
    const hasEmpty = newGrid.some(r => r.some(v => v === 0));
    if (!hasEmpty) {
      let canMove = false;
      for (let r = 0; r < 4 && !canMove; r++) for (let c = 0; c < 4 && !canMove; c++) {
        if ((r < 3 && newGrid[r][c] === newGrid[r + 1][c]) || (c < 3 && newGrid[r][c] === newGrid[r][c + 1])) canMove = true;
      }
      if (!canMove) { setG2048Over(true); toast.error('Game over! Final: ' + (g2048Score + gained)); }
    }
  };
  // Keyboard controls for 2048
  useEffect(() => {
    if (!showGame || game?.id !== '2048') return;
    const handler = (e) => {
      const map = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
      if (map[e.key]) { e.preventDefault(); g2048Move(map[e.key]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showGame, game, g2048, g2048Over]);

  // ═══ Coin Streak ═══
  const initCoinStreak = () => {
    setCoinFace(null); setCoinStreak(0); setCoinFlipping(false); setCoinHistory([]);
    try { setCoinBest(parseInt(localStorage.getItem('hub_coin_best') || '0', 10) || 0); } catch { setCoinBest(0); }
  };
  const flipCoin = (guess) => {
    if (coinFlipping) return;
    setCoinFlipping(true);
    setTimeout(() => {
      const outcome = Math.random() < 0.5 ? 'H' : 'T';
      setCoinFace(outcome);
      setCoinHistory(h => [outcome, ...h].slice(0, 10));
      if (outcome === guess) {
        setCoinStreak(s => {
          const ns = s + 1;
          if (ns > coinBest) { setCoinBest(ns); try { localStorage.setItem('hub_coin_best', String(ns)); } catch {} }
          setScores(sc => ({ ...sc, 'coin-streak': Math.max(sc['coin-streak'] || 0, ns) }));
          if (ns === 5) toast.success('🔥 5 in a row!');
          else if (ns === 10) toast.success('⚡ 10 in a row — you\'re on fire!');
          return ns;
        });
      } else {
        if (coinStreak > 0) toast.error(`Streak broken at ${coinStreak}!`);
        setCoinStreak(0);
      }
      setCoinFlipping(false);
    }, 650);
  };

  // Multiplayer functions
  const sendGameInvite = async () => {
    if (!inviteTarget || !selectedMultiplayerGame) return;
    try {
      const h = getAuthHeaders();
      await axios.post(`${BACKEND_URL}/api/games/invite`, {
        game_type: selectedMultiplayerGame.id,
        opponent_username: inviteTarget,
      }, { headers: h });
      toast.success('Game invite sent via DM!');
      setShowInviteModal(false);
      setInviteTarget('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send invite'); }
  };

  const respondToInvite = async (gameId, action) => {
    try {
      const h = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/games/invites/${gameId}/respond`, { action }, { headers: h });
      if (action === 'accept') {
        toast.success('Game accepted! Let\'s play.');
        setActiveMultiplayerGame(gameId);
        setMpGameState(res.data.game_state);
        setShowMultiplayerGame(true);
      } else {
        toast.info('Invite declined');
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openActiveGame = async (gameId) => {
    try {
      const h = getAuthHeaders();
      const res = await axios.get(`${BACKEND_URL}/api/games/state/${gameId}`, { headers: h });
      setActiveMultiplayerGame(gameId);
      setMpGameState(res.data.game_state);
      setSelectedMultiplayerGame(MULTIPLAYER_GAMES.find(g => g.id === res.data.game_type));
      setShowMultiplayerGame(true);
      setRpsChoice(null);
      setWordDuelInput('');
    } catch (e) { toast.error('Failed to load game'); }
  };

  const makeMove = async (move) => {
    try {
      const h = getAuthHeaders();
      const res = await axios.post(`${BACKEND_URL}/api/games/move/${activeMultiplayerGame}`, { move }, { headers: h });
      setMpGameState(res.data.game_state);
      if (res.data.game_state?.result) {
        fetchData();
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const pendingForMe = gameInvites.filter(i => i.opponent_username === currentUser?.username && i.status === 'pending');
  const activeGames = gameInvites.filter(i => i.status === 'active');

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <Sparkles className="w-8 h-8 text-purple-400 animate-pulse mx-auto mb-3" />
        <p className="text-white/50">Loading...</p>
      </div>
    </div>
  );

  const pending = pendingKudos.length + pendingPolls.length;

  const HUB_TABS = [
    { id: 'overview', label: 'Overview', icon: Sparkles },
    { id: 'recognition', label: 'Recognition', icon: Award },
    { id: 'productivity', label: 'Productivity', icon: CheckSquare },
    { id: 'engagement', label: 'Engagement', icon: MessageSquare },
    { id: 'games', label: 'Games', icon: Gamepad2, badge: pendingForMe.length || null },
    { id: 'awards', label: 'Awards', icon: Trophy },
  ];

  // Personal greeting helpers
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 5 ? 'Still up' : hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : hour < 21 ? 'Good evening' : 'Good night';
  const firstName = currentUser?.first_name || (currentUser?.display_name || currentUser?.username || 'there').split(' ')[0];
  const myRank = leaderboard.findIndex(e => e.username === currentUser?.username);
  const kudosReceived = leaderboard.find(e => e.username === currentUser?.username)?.kudos_count || 0;
  const badgesEarned = gamBadges.earned?.length || 0;
  const badgesTotal = gamBadges.all_badges?.length || 0;
  const nextBadgePct = badgesTotal ? Math.min(100, Math.round((badgesEarned / badgesTotal) * 100)) : 0;

  return (
    <div className="space-y-6 max-w-[1600px] mx-auto" data-testid="staff-hub-page">
      {/* ═══ LUXURY GREETING HERO ═══ */}
      <div className="relative overflow-hidden rounded-[28px] border border-white/[0.06] p-6 lg:p-8"
        style={{ background: 'linear-gradient(135deg, rgba(10,10,20,0.95) 0%, rgba(15,10,30,0.98) 55%, rgba(8,12,24,0.95) 100%)' }}>
        {/* Ambient glow */}
        <div className="pointer-events-none absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full blur-[130px] opacity-40" style={{ background: 'radial-gradient(circle, #8A2BE2 0%, transparent 70%)' }} />
        <div className="pointer-events-none absolute -bottom-32 -right-32 w-[520px] h-[420px] rounded-full blur-[130px] opacity-30" style={{ background: 'radial-gradient(circle, #00E5FF 0%, transparent 70%)' }} />
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '38px 38px' }} />
        {/* Top hairline */}
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(0,229,255,0.5) 50%, transparent 100%)' }} />

        <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-6 items-center">
          {/* Left: greeting */}
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-white/50 text-[10px] uppercase tracking-[0.32em] mb-3">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(0,229,255,0.9)] animate-pulse" />
              {now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            <h1 className="text-3xl lg:text-5xl font-light tracking-tight text-white leading-[1.05]">
              {greeting}, <span className="font-serif italic bg-gradient-to-r from-[#00E5FF] via-white to-[#8A2BE2] bg-clip-text text-transparent">{firstName}</span>
            </h1>
            <p className="mt-3 text-white/45 text-sm max-w-xl">A curated space to celebrate wins, sync your team, and rise up the ranks. Everything you need to shine — in one hub.</p>

            {/* Micro chips */}
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {currentUser?.role && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/[0.08] text-white/70 text-[11px] tracking-wide">
                  <Crown className="w-3 h-3 text-amber-300" /> {currentUser.role}
                </span>
              )}
              {myRank >= 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/[0.08] border border-amber-500/25 text-amber-200 text-[11px] tracking-wide">
                  <TrendingUp className="w-3 h-3" /> Rank #{myRank + 1}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pink-500/[0.08] border border-pink-500/25 text-pink-200 text-[11px] tracking-wide">
                <Award className="w-3 h-3" /> {kudosReceived} kudos
              </span>
              {pending > 0 && isExec && (
                <button onClick={() => setShowApprovals(true)} data-testid="pending-approvals-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/[0.10] border border-orange-500/30 text-orange-200 text-[11px] tracking-wide hover:bg-orange-500/[0.16] transition-all">
                  <AlertCircle className="w-3 h-3 animate-pulse" /> {pending} awaiting review
                </button>
              )}
            </div>
          </div>

          {/* Right: Points ring + CTAs */}
          <div className="flex items-center gap-5 justify-end flex-wrap">
            {/* Points ring */}
            <div className="relative w-28 h-28 flex-shrink-0">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="hubRing" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#00E5FF" />
                    <stop offset="100%" stopColor="#8A2BE2" />
                  </linearGradient>
                </defs>
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="url(#hubRing)" strokeWidth="6" strokeLinecap="round"
                  strokeDasharray={`${(nextBadgePct / 100) * 264} 264`} style={{ filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.5))' }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-2xl font-light text-white tabular-nums leading-none">{gamPoints.points || 0}</p>
                <p className="text-[9px] tracking-[0.22em] uppercase text-white/40 mt-1">pts</p>
              </div>
            </div>

            {/* Vertical divider + stats */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-purple-500/15 border border-purple-500/25"><Trophy className="w-3 h-3 text-purple-300" /></span>
                <span className="tabular-nums">{badgesEarned}<span className="text-white/30">/{badgesTotal || '—'}</span> badges</span>
              </div>
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-cyan-500/15 border border-cyan-500/25"><Vote className="w-3 h-3 text-cyan-300" /></span>
                <span className="tabular-nums">{stats.active_polls || 0} active polls</span>
              </div>
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-emerald-500/15 border border-emerald-500/25"><Users className="w-3 h-3 text-emerald-300" /></span>
                <span className="tabular-nums">{stats.total_staff || 0} teammates</span>
              </div>
            </div>

            {/* Primary CTAs */}
            {canManage && (
              <div className="flex flex-col gap-2 min-w-[140px]">
                <button onClick={() => setShowKudos(true)} data-testid="send-kudos-btn"
                  className="group relative inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-[12px] font-medium text-black overflow-hidden transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #F43F5E 0%, #F97316 100%)', boxShadow: '0 10px 30px -10px rgba(244,63,94,0.6)' }}>
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Award className="w-3.5 h-3.5" /> Send kudos
                </button>
                <button onClick={() => setShowPoll(true)} data-testid="create-poll-btn"
                  className="group relative inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-[12px] font-medium text-black overflow-hidden transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #00E5FF 0%, #8A2BE2 100%)', boxShadow: '0 10px 30px -10px rgba(0,229,255,0.55)' }}>
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Vote className="w-3.5 h-3.5" /> New poll
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ MODERN TAB RAIL ═══ */}
      <div className="relative">
        <div className="flex items-center gap-1 overflow-x-auto pb-1 jewel-scroll" data-testid="hub-tabs">
          {HUB_TABS.map(tab => {
            const active = hubTab === tab.id;
            return (
              <button key={tab.id} onClick={() => { setHubTab(tab.id); setExpandedSection(null); }} data-testid={`hub-tab-${tab.id}`}
                className={`group relative inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[12px] font-medium tracking-wide transition-all whitespace-nowrap ${
                  active ? 'text-white' : 'text-white/45 hover:text-white/80'
                }`}
                style={active ? {
                  background: 'linear-gradient(135deg, rgba(0,229,255,0.16), rgba(138,43,226,0.14))',
                  border: '1px solid rgba(255,255,255,0.14)',
                  boxShadow: '0 0 24px -8px rgba(0,229,255,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
                } : { border: '1px solid transparent' }}>
                <tab.icon className={`w-3.5 h-3.5 transition-transform ${active ? 'text-cyan-300' : 'group-hover:scale-110'}`} />
                {tab.label}
                {tab.badge > 0 && <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold shadow-[0_0_10px_rgba(244,63,94,0.6)] animate-pulse">{tab.badge}</span>}
                {active && <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-[2px] rounded-full" style={{ background: 'linear-gradient(90deg, #00E5FF, #8A2BE2)', boxShadow: '0 0 8px rgba(0,229,255,0.6)' }} />}
              </button>
            );
          })}
        </div>
      </div>

      {hubTab === 'overview' && <>

      {/* Today at a Glance */}
      <TodayGlanceWidget
        getAuthHeaders={getAuthHeaders}
        currentUser={currentUser}
        onNavigate={(target) => {
          if (target === 'tasks') { setHubTab('productivity'); setExpandedSection('tasks'); return; }
          if (target === 'shift' || target === 'leave') { window.location.href = '/staff/leave-management'; return; }
          if (target === 'directory') { window.location.href = '/staff/staff-directory'; return; }
        }}
      />

      {/* ═══ KUDOS WALL + PODIUM BENTO ═══ */}
      <div className="grid lg:grid-cols-12 gap-4">
        {/* Kudos Wall */}
        <div className="lg:col-span-8 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0a14] to-[#0d0a1c] p-5 min-h-[280px]">
          <div className="pointer-events-none absolute -top-20 -right-20 w-56 h-56 rounded-full blur-[100px]" style={{ background: 'rgba(244,63,94,0.14)' }} />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(244,63,94,0.25), rgba(249,115,22,0.18))', boxShadow: '0 0 16px -6px rgba(244,63,94,0.5)' }}>
                <Heart className="w-4 h-4 text-rose-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Recognition</p>
                <h3 className="text-white text-lg font-light">Kudos Wall</h3>
              </div>
            </div>
            {kudosFeed.length > 0 && <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/[0.10] border border-rose-500/25 text-rose-200 text-[10px] font-medium tracking-wide">{kudosFeed.length} this month</span>}
          </div>
          {kudosFeed.length === 0 ? (
            <div className="relative text-center py-10">
              <PartyPopper className="w-10 h-10 mx-auto text-white/25 mb-2" />
              <p className="text-white/50 text-sm">No kudos yet — be the first to shine.</p>
            </div>
          ) : (
            <div className="relative space-y-2 max-h-[280px] overflow-y-auto pr-1 jewel-scroll">
              {kudosFeed.slice(0, 6).map(k => {
                const cat = KUDOS_CATEGORIES[k.category] || KUDOS_CATEGORIES.teamwork;
                return (
                  <div key={k.id} className="group flex items-start gap-3 p-3 rounded-2xl border border-white/[0.05] bg-white/[0.02] hover:border-white/[0.14] transition-all">
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${cat.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <cat.icon className="w-4 h-4 text-white" strokeWidth={1.8} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white/90 text-sm">
                        <span className="font-medium">{k.sender_name}</span>
                        <span className="text-white/30 mx-1.5">→</span>
                        <span className="font-medium text-cyan-300">{k.recipient_name}</span>
                      </p>
                      <p className="text-white/50 text-xs mt-1 italic line-clamp-2">"{k.message}"</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[9px] tracking-[0.14em] uppercase font-semibold px-1.5 py-0.5 rounded-md border" style={{ borderColor: 'rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)', color: 'rgba(255,255,255,0.55)' }}>{cat.label}</span>
                        <span className="text-white/25 text-[10px]">{k.created_at && new Date(k.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Podium (top 3) */}
        <div className="lg:col-span-4 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0a14] to-[#12100a] p-5 min-h-[280px]">
          <div className="pointer-events-none absolute -top-20 -left-20 w-56 h-56 rounded-full blur-[100px]" style={{ background: 'rgba(251,191,36,0.16)' }} />
          <div className="relative flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.30), rgba(249,115,22,0.18))', boxShadow: '0 0 16px -6px rgba(251,191,36,0.6)' }}>
              <Crown className="w-4 h-4 text-amber-300" strokeWidth={1.8} />
            </div>
            <div>
              <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Leaderboard</p>
              <h3 className="text-white text-lg font-light">The Podium</h3>
            </div>
          </div>
          {leaderboard.length === 0 ? (
            <div className="relative text-center py-10"><Trophy className="w-10 h-10 mx-auto text-white/25 mb-2" /><p className="text-white/50 text-sm">Rankings coming soon</p></div>
          ) : (
            <div className="relative">
              {/* Top-3 podium visual */}
              <div className="grid grid-cols-3 gap-2 items-end mb-4 h-[110px]">
                {[1, 0, 2].map(idx => {
                  const p = leaderboard[idx];
                  if (!p) return <div key={idx} />;
                  const isFirst = idx === 0;
                  const h = isFirst ? 'h-[100px]' : idx === 1 ? 'h-[80px]' : 'h-[62px]';
                  const grad = isFirst ? 'from-amber-400 to-orange-500' : idx === 1 ? 'from-slate-300 to-slate-500' : 'from-orange-400 to-rose-500';
                  const shadow = isFirst ? 'shadow-[0_0_24px_-4px_rgba(251,191,36,0.6)]' : '';
                  return (
                    <div key={p.username} className="flex flex-col items-center gap-1.5">
                      <div className={`relative w-11 h-11 rounded-full bg-gradient-to-br ${grad} flex items-center justify-center text-white font-semibold text-sm ${shadow}`}>
                        {(p.display_name || p.username).charAt(0).toUpperCase()}
                        {isFirst && <Crown className="absolute -top-3 left-1/2 -translate-x-1/2 w-4 h-4 text-amber-300 drop-shadow-[0_0_6px_rgba(251,191,36,0.9)]" />}
                      </div>
                      <p className="text-white/90 text-[10px] text-center truncate max-w-full font-medium">{(p.display_name || p.username).split(' ')[0]}</p>
                      <div className={`w-full ${h} rounded-t-xl bg-gradient-to-t ${grad} opacity-70 border-t border-l border-r border-white/10 flex items-start justify-center pt-1.5`}>
                        <span className="text-[10px] text-white/90 font-bold">{idx + 1}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Runners-up */}
              <div className="space-y-1">
                {leaderboard.slice(3, 6).map((e, i) => (
                  <div key={e.username} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                    <span className="w-5 text-center text-white/40 text-[10px] font-mono">{i + 4}</span>
                    <span className="flex-1 text-white/80 text-xs truncate">{e.display_name || e.username}</span>
                    <span className="inline-flex items-center gap-1 text-rose-300 text-xs"><Award className="w-3 h-3" /><span className="tabular-nums font-medium">{e.kudos_count}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ FOCUS + UPCOMING + AWARDS BENTO ═══ */}
      <div className="grid lg:grid-cols-12 gap-4">
        {/* Today's Focus (Tasks) */}
        <div className="lg:col-span-4 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0d1a] to-[#0a0d1a] p-5">
          <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full blur-[80px]" style={{ background: 'rgba(59,130,246,0.14)' }} />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.18)', boxShadow: '0 0 16px -6px rgba(59,130,246,0.6)' }}>
                <Target className="w-4 h-4 text-blue-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Today</p>
                <h3 className="text-white text-base font-light">Your Focus</h3>
              </div>
            </div>
            <button onClick={() => setExpandedSection('tasks')} data-testid="expand-tasks-btn" className="w-7 h-7 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-all">
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="relative space-y-1.5">
            {tasks.filter(t => t.status !== 'completed').slice(0, 4).length === 0 ? (
              <div className="text-center py-6 text-white/40 text-xs"><CheckSquare className="w-8 h-8 mx-auto mb-1.5 opacity-25" />Inbox zero. Nice.</div>
            ) : (
              tasks.filter(t => t.status !== 'completed').slice(0, 4).map(t => (
                <div key={t.id} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.12] transition-all">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 shadow-[0_0_8px_currentColor] ${t.priority === 'high' || t.priority === 'urgent' ? 'bg-rose-400 text-rose-400' : t.priority === 'medium' ? 'bg-amber-400 text-amber-400' : 'bg-emerald-400 text-emerald-400'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-white/90 text-xs truncate">{t.title}</p>
                    <p className="text-white/35 text-[10px]">{t.assigned_to || 'Unassigned'}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Upcoming (Meetings) */}
        <div className="lg:col-span-4 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0d1a] to-[#100a1a] p-5">
          <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full blur-[80px]" style={{ background: 'rgba(244,63,94,0.14)' }} />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(244,63,94,0.18)', boxShadow: '0 0 16px -6px rgba(244,63,94,0.55)' }}>
                <CalendarDays className="w-4 h-4 text-rose-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Diary</p>
                <h3 className="text-white text-base font-light">Upcoming</h3>
              </div>
            </div>
            <button onClick={() => setExpandedSection('meetings')} data-testid="expand-meetings-btn" className="w-7 h-7 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-all">
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="relative space-y-1.5">
            {meetings.slice(0, 4).length === 0 ? (
              <div className="text-center py-6 text-white/40 text-xs"><CalendarDays className="w-8 h-8 mx-auto mb-1.5 opacity-25" />No meetings scheduled</div>
            ) : (
              meetings.slice(0, 4).map(m => (
                <div key={m.id} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.12] transition-all">
                  <div className="w-9 h-9 rounded-lg bg-rose-500/[0.10] border border-rose-500/25 flex flex-col items-center justify-center flex-shrink-0">
                    <span className="text-[8px] tracking-widest uppercase text-rose-300/70">{m.date && new Date(m.date).toLocaleDateString('en-GB', { month: 'short' })}</span>
                    <span className="text-rose-200 text-xs font-semibold leading-none">{m.date && new Date(m.date).getDate()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/90 text-xs truncate">{m.title}</p>
                    <p className="text-white/35 text-[10px]">{m.time || 'TBC'} · {m.room || 'Remote'}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Awards Showcase */}
        <div className="lg:col-span-4 relative overflow-hidden rounded-[24px] border border-amber-400/20 bg-gradient-to-br from-[#12100a] via-[#0f0d15] to-[#0a0a14] p-5">
          <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full blur-[90px]" style={{ background: 'rgba(251,191,36,0.20)' }} />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.28), rgba(249,115,22,0.18))', boxShadow: '0 0 16px -4px rgba(251,191,36,0.6)' }}>
                <Trophy className="w-4 h-4 text-amber-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Honours</p>
                <h3 className="text-white text-base font-light">Awards</h3>
              </div>
            </div>
            <button onClick={() => setHubTab('awards')} className="w-7 h-7 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-all">
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="relative">
            {sotm.current?.winner || sotm.current?.username ? (
              <div className="relative overflow-hidden rounded-2xl p-4 mb-3 border border-amber-400/25" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.10), rgba(139,92,246,0.06))' }}>
                <Crown className="absolute -top-2 -right-2 w-14 h-14 text-amber-300/20" />
                <p className="text-[9px] tracking-[0.28em] uppercase text-amber-200/70 mb-1">Staff of the Month</p>
                <p className="text-white text-lg font-medium">{sotm.current.display_name || sotm.current.winner || sotm.current.username}</p>
                <p className="text-white/50 text-xs mt-1">{sotm.current.reason || 'Outstanding contribution'}</p>
              </div>
            ) : (
              <div className="rounded-2xl p-4 mb-3 border border-white/[0.05] bg-white/[0.02]">
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40 mb-1">Staff of the Month</p>
                <p className="text-white/50 text-sm">Nominations open</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                <p className="text-[9px] tracking-[0.22em] uppercase text-white/40 mb-1">My points</p>
                <p className="text-white text-xl font-light tabular-nums">{gamPoints.points || 0}</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                <p className="text-[9px] tracking-[0.22em] uppercase text-white/40 mb-1">Badges</p>
                <p className="text-white text-xl font-light tabular-nums">{badgesEarned}<span className="text-white/30 text-sm">/{badgesTotal || '—'}</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ POLLS + ANNOUNCEMENTS + FEEDBACK BENTO ═══ */}
      <div className="grid lg:grid-cols-12 gap-4">
        {/* Active Poll spotlight */}
        <div className="lg:col-span-5 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0d1a] to-[#0a1218] p-5">
          <div className="pointer-events-none absolute -top-16 -left-16 w-48 h-48 rounded-full blur-[80px]" style={{ background: 'rgba(0,229,255,0.16)' }} />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,229,255,0.16)', boxShadow: '0 0 14px -4px rgba(0,229,255,0.6)' }}>
                <Vote className="w-4 h-4 text-cyan-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Team voice</p>
                <h3 className="text-white text-base font-light">Live Polls</h3>
              </div>
            </div>
            {polls.length > 0 && <span className="text-[10px] text-cyan-300/70">{polls.length} active</span>}
          </div>
          {polls.length === 0 ? (
            <div className="relative text-center py-8 text-white/40 text-xs"><Vote className="w-8 h-8 mx-auto mb-2 opacity-25" />No live polls right now</div>
          ) : (
            <div className="relative space-y-3 max-h-[240px] overflow-y-auto pr-1 jewel-scroll">
              {polls.slice(0, 2).map(p => {
                const totalVotes = p.total_votes || p.options?.reduce((s, o) => s + (o.votes || 0), 0) || 0;
                const hasVoted = p.user_voted || p.voted_by?.includes(currentUser?.username);
                return (
                  <div key={p.id} className="p-3 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
                    <p className="text-white/90 text-sm mb-2 font-medium">{p.question}</p>
                    <div className="space-y-1.5">
                      {p.options?.map((o, i) => {
                        const pct = o.percentage ?? (totalVotes > 0 ? Math.round(((o.votes || 0) / totalVotes) * 100) : 0);
                        return (
                          <button key={i} onClick={() => !hasVoted && vote(p.id, i)} disabled={hasVoted}
                            className={`relative w-full text-left p-2 rounded-lg overflow-hidden text-xs transition-all ${hasVoted ? 'cursor-default' : 'hover:bg-white/[0.03]'}`}
                            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                            {hasVoted && <div className="absolute inset-y-0 left-0 rounded-lg opacity-70" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, rgba(0,229,255,0.18), rgba(138,43,226,0.12))' }} />}
                            <div className="relative flex justify-between">
                              <span className="text-white/85">{o.text || o}</span>
                              {hasVoted && <span className="text-cyan-300 font-semibold tabular-nums">{pct}%</span>}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-white/30 text-[10px] mt-2">{totalVotes} votes · by {p.created_by_name}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Announcements ticker */}
        <div className="lg:col-span-4 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#12100a] to-[#0a0d1a] p-5">
          <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full blur-[80px]" style={{ background: 'rgba(249,115,22,0.14)' }} />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(249,115,22,0.18)', boxShadow: '0 0 14px -4px rgba(249,115,22,0.55)' }}>
                <Megaphone className="w-4 h-4 text-orange-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Broadcasts</p>
                <h3 className="text-white text-base font-light">Announcements</h3>
              </div>
            </div>
            <button onClick={() => setExpandedSection('announcements')} data-testid="expand-announcements-btn" className="w-7 h-7 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-all">
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="relative space-y-1.5 max-h-[240px] overflow-y-auto pr-1 jewel-scroll">
            {announcements.slice(0, 4).length === 0 ? (
              <div className="text-center py-6 text-white/40 text-xs"><Megaphone className="w-8 h-8 mx-auto mb-1.5 opacity-25" />Quiet on the wire</div>
            ) : (
              announcements.slice(0, 4).map(a => (
                <div key={a.id} className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <div className="flex items-center gap-2 mb-1">
                    {a.priority === 'urgent' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse shadow-[0_0_8px_rgba(244,63,94,0.9)]" />}
                    <p className="text-white/90 text-xs font-medium truncate flex-1">{a.title}</p>
                  </div>
                  <p className="text-white/45 text-[11px] line-clamp-2">{a.content}</p>
                  <p className="text-white/25 text-[9px] mt-1">{a.author} · {a.created_at && new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Feedback / Ideas */}
        <div className="lg:col-span-3 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a1416] to-[#0a0d1a] p-5">
          <div className="pointer-events-none absolute -bottom-16 -left-16 w-48 h-48 rounded-full blur-[80px]" style={{ background: 'rgba(20,184,166,0.12)' }} />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(20,184,166,0.15)', boxShadow: '0 0 14px -4px rgba(20,184,166,0.55)' }}>
                <MessageSquarePlus className="w-4 h-4 text-teal-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Voice</p>
                <h3 className="text-white text-base font-light">Ideas</h3>
              </div>
            </div>
            <button onClick={() => setExpandedSection('feedback')} data-testid="expand-feedback-btn" className="w-7 h-7 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-all">
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="relative space-y-1.5 max-h-[240px] overflow-y-auto pr-1 jewel-scroll">
            {feedbackList.slice(0, 4).length === 0 ? (
              <div className="text-center py-6 text-white/40 text-xs"><Lightbulb className="w-8 h-8 mx-auto mb-1.5 opacity-25" />Share your first idea</div>
            ) : (
              feedbackList.slice(0, 4).map(f => (
                <div key={f.id} className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                  <p className="text-white/90 text-xs truncate">{f.title || f.message?.substring(0, 50)}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className={`text-[9px] tracking-wide px-1.5 py-0.5 rounded-md border ${f.category === 'bug' ? 'bg-rose-500/[0.10] border-rose-500/25 text-rose-200' : f.category === 'feature' ? 'bg-blue-500/[0.10] border-blue-500/25 text-blue-200' : 'bg-white/[0.03] border-white/[0.08] text-white/50'}`}>{f.category || 'general'}</span>
                    <span className="text-white/30 text-[9px]">{f.author || 'Anonymous'}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>


      </>}

      {/* ═══ RECOGNITION TAB — Kudos Wall + Podium ═══ */}
      {hubTab === 'recognition' && (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Full Kudos Wall */}
          <div className="lg:col-span-2 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0a14] to-[#0d0a1c] p-5">
            <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full blur-[120px]" style={{ background: 'rgba(244,63,94,0.14)' }} />
            <div className="relative flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(244,63,94,0.25), rgba(249,115,22,0.18))', boxShadow: '0 0 18px -4px rgba(244,63,94,0.6)' }}>
                  <Heart className="w-4 h-4 text-rose-300" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Celebration wall</p>
                  <h3 className="text-white text-xl font-light">Every <span className="italic font-serif text-rose-300">kudos</span> ever given</h3>
                </div>
              </div>
              {canManage && (
                <button onClick={() => setShowKudos(true)} className="group relative inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-medium text-black overflow-hidden transition-all hover:-translate-y-0.5"
                  style={{ background: 'linear-gradient(135deg, #F43F5E 0%, #F97316 100%)', boxShadow: '0 10px 30px -10px rgba(244,63,94,0.6)' }}>
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  <Award className="w-3.5 h-3.5" /> Send kudos
                </button>
              )}
            </div>
            {kudosFeed.length === 0 ? (
              <div className="relative text-center py-14">
                <PartyPopper className="w-12 h-12 mx-auto text-white/25 mb-3" />
                <p className="text-white/60 text-sm">No kudos yet — be the first to shine.</p>
              </div>
            ) : (
              <div className="relative grid gap-2 max-h-[560px] overflow-y-auto pr-1 jewel-scroll">
                {kudosFeed.map(k => {
                  const cat = KUDOS_CATEGORIES[k.category] || KUDOS_CATEGORIES.teamwork;
                  return (
                    <div key={k.id} className="group relative overflow-hidden p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.14] transition-all">
                      <span className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${cat.gradient}`} />
                      <div className="flex items-start gap-3">
                        <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${cat.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                          <cat.icon className="w-5 h-5 text-white" strokeWidth={1.8} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm">
                            <span className="font-medium">{k.sender_name}</span>
                            <span className="text-white/30 mx-1.5">→</span>
                            <span className="font-medium text-cyan-300">{k.recipient_name}</span>
                          </p>
                          <span className="inline-flex items-center px-1.5 py-0.5 mt-1 rounded-md text-[9px] font-semibold tracking-[0.12em] uppercase border" style={{ color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.08)' }}>{cat.label}</span>
                          <p className="text-white/70 text-sm mt-2 italic">"{k.message}"</p>
                        </div>
                        <span className="text-[10px] text-white/30 whitespace-nowrap">{k.created_at && new Date(k.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Full Leaderboard */}
          <div className="relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0a14] to-[#12100a] p-5">
            <div className="pointer-events-none absolute -top-20 -left-20 w-64 h-64 rounded-full blur-[120px]" style={{ background: 'rgba(251,191,36,0.18)' }} />
            <div className="relative flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.30), rgba(249,115,22,0.20))', boxShadow: '0 0 18px -4px rgba(251,191,36,0.7)' }}>
                <Crown className="w-4 h-4 text-amber-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Hall of fame</p>
                <h3 className="text-white text-xl font-light">Leaderboard</h3>
              </div>
            </div>
            {leaderboard.length === 0 ? (
              <div className="relative text-center py-14"><Trophy className="w-12 h-12 mx-auto text-white/25 mb-3" /><p className="text-white/60 text-sm">Rankings coming soon</p></div>
            ) : (
              <div className="relative space-y-1.5">
                {leaderboard.map((e, i) => {
                  const isMe = e.username === currentUser?.username;
                  const rankColour = i === 0 ? 'from-amber-400 to-orange-500' : i === 1 ? 'from-slate-300 to-slate-500' : i === 2 ? 'from-orange-400 to-rose-500' : 'from-slate-700 to-slate-800';
                  return (
                    <div key={e.username} className={`relative flex items-center gap-3 p-2.5 rounded-xl transition-all ${
                      isMe ? 'border border-cyan-400/30 bg-cyan-500/[0.05]' : i < 3 ? 'border border-white/[0.08] bg-white/[0.02]' : 'border border-transparent hover:bg-white/[0.02]'
                    }`}>
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${rankColour} flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}>
                        {i === 0 ? <Crown className="w-3.5 h-3.5" /> : i + 1}
                      </div>
                      <span className="flex-1 text-white/85 text-sm truncate">{e.display_name || e.username}{isMe && <span className="text-cyan-300 text-[10px] ml-1.5">· you</span>}</span>
                      <span className="inline-flex items-center gap-1 text-rose-300 text-xs"><Award className="w-3 h-3" /><span className="font-semibold tabular-nums">{e.kudos_count}</span></span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ PRODUCTIVITY TAB — Jewel bento with Quick Add + Progress ═══ */}
      {hubTab === 'productivity' && !expandedSection && (
        <div className="space-y-4">
          {/* Progress strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {(() => {
              const openTasks = tasks.filter(t => t.status !== 'completed').length;
              const doneTasks = tasks.filter(t => t.status === 'completed').length;
              const total = tasks.length || 1;
              const donePct = Math.round((doneTasks / total) * 100);
              const urgent = tasks.filter(t => (t.priority === 'high' || t.priority === 'urgent') && t.status !== 'completed').length;
              return [
                { label: 'Open tasks', value: openTasks, hex: '#3B82F6', icon: CheckSquare, sub: `${doneTasks} done` },
                { label: 'On fire', value: urgent, hex: '#F43F5E', icon: Rocket, sub: 'urgent + high' },
                { label: 'This week', value: meetings.length, hex: '#F97316', icon: CalendarDays, sub: 'meetings' },
                { label: 'Completion', value: `${donePct}%`, hex: '#10B981', icon: TrendingUp, sub: `${doneTasks}/${total}` },
              ].map((k, i) => (
                <div key={i} className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="pointer-events-none absolute -top-10 -right-10 w-32 h-32 rounded-full blur-[70px] opacity-40" style={{ background: k.hex }} />
                  <div className="relative flex items-start justify-between">
                    <div>
                      <p className="text-[9px] tracking-[0.22em] uppercase text-white/40 mb-1">{k.label}</p>
                      <p className="text-2xl font-light text-white tabular-nums leading-none">{k.value}</p>
                      <p className="text-white/40 text-[10px] mt-1.5">{k.sub}</p>
                    </div>
                    <k.icon className="w-4 h-4" style={{ color: k.hex }} strokeWidth={1.8} />
                  </div>
                </div>
              ));
            })()}
          </div>

          <div className="grid lg:grid-cols-5 gap-4">
            {/* Tasks — big luxury tile */}
            <div className="lg:col-span-3 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0d1a] to-[#0a0f1e] p-5">
              <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full blur-[110px]" style={{ background: 'rgba(59,130,246,0.15)' }} />
              <div className="relative flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.28), rgba(0,229,255,0.14))', boxShadow: '0 0 18px -4px rgba(59,130,246,0.55)' }}>
                    <CheckSquare className="w-4 h-4 text-blue-300" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Do list</p>
                    <h3 className="text-white text-xl font-light">Task <span className="italic font-serif text-blue-300">command</span></h3>
                  </div>
                </div>
                <button onClick={() => setExpandedSection('tasks')} data-testid="expand-tasks-btn"
                  className="group relative inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-medium text-white overflow-hidden border border-white/10 hover:border-blue-400/40 transition-all">
                  <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                  Full command <ArrowRight className="w-3 h-3" />
                </button>
              </div>
              <div className="relative space-y-1.5 max-h-[420px] overflow-y-auto pr-1 jewel-scroll">
                {tasks.length === 0 ? (
                  <div className="text-center py-10 text-white/40 text-sm"><CheckSquare className="w-10 h-10 mx-auto mb-2 opacity-25" />Inbox zero. Draft your first task.</div>
                ) : (
                  tasks.map(t => (
                    <div key={t.id} className="group flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-blue-400/25 transition-all">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 shadow-[0_0_10px_currentColor] ${t.priority === 'high' || t.priority === 'urgent' ? 'bg-rose-400 text-rose-400' : t.priority === 'medium' ? 'bg-amber-400 text-amber-400' : 'bg-emerald-400 text-emerald-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${t.status === 'completed' ? 'text-white/40 line-through' : 'text-white/90'}`}>{t.title}</p>
                        <p className="text-white/40 text-[10px]">{t.assigned_to || 'Unassigned'}{t.due_date ? ' · due ' + new Date(t.due_date).toLocaleDateString('en-GB', { day:'numeric', month:'short' }) : ''}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-md text-[9px] font-semibold tracking-[0.12em] uppercase border ${t.status === 'completed' ? 'bg-emerald-500/[0.10] border-emerald-500/25 text-emerald-200' : t.status === 'in_progress' ? 'bg-blue-500/[0.10] border-blue-500/25 text-blue-200' : 'bg-white/[0.03] border-white/[0.08] text-white/60'}`}>{(t.status || 'todo').replace('_', ' ')}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Meetings */}
            <div className="lg:col-span-2 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#100a1a] to-[#0a0d1a] p-5">
              <div className="pointer-events-none absolute -top-24 -left-24 w-72 h-72 rounded-full blur-[110px]" style={{ background: 'rgba(244,63,94,0.14)' }} />
              <div className="relative flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(244,63,94,0.24), rgba(249,115,22,0.16))', boxShadow: '0 0 18px -4px rgba(244,63,94,0.55)' }}>
                    <CalendarDays className="w-4 h-4 text-rose-300" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Diary</p>
                    <h3 className="text-white text-xl font-light">Meetings</h3>
                  </div>
                </div>
                <button onClick={() => setExpandedSection('meetings')}
                  className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-all">
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="relative space-y-1.5 max-h-[420px] overflow-y-auto pr-1 jewel-scroll">
                {meetings.length === 0 ? (
                  <div className="text-center py-10 text-white/40 text-sm"><CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-25" />Clear diary. Bliss.</div>
                ) : (
                  meetings.map(m => (
                    <div key={m.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/[0.05] hover:border-rose-400/25 transition-all">
                      <div className="w-11 h-11 rounded-xl bg-rose-500/[0.10] border border-rose-500/25 flex flex-col items-center justify-center flex-shrink-0">
                        <span className="text-[8px] tracking-widest uppercase text-rose-300/70 leading-none">{m.date && new Date(m.date).toLocaleDateString('en-GB', { month: 'short' })}</span>
                        <span className="text-rose-200 text-sm font-semibold leading-tight tabular-nums">{m.date && new Date(m.date).getDate()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/90 text-sm truncate">{m.title}</p>
                        <p className="text-white/40 text-[10px]">{m.time || 'TBC'} · {m.room || 'Remote'}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {hubTab === 'productivity' && expandedSection === 'tasks' && <TasksPage getAuthHeaders={getAuthHeaders} currentUser={currentUser} onBack={() => setExpandedSection(null)} />}
      {hubTab === 'productivity' && expandedSection === 'meetings' && <MeetingsPage getAuthHeaders={getAuthHeaders} currentUser={currentUser} onBack={() => setExpandedSection(null)} />}

      {/* ═══ ENGAGEMENT TAB — Polls · Announcements · Ideas + HR ═══ */}
      {hubTab === 'engagement' && !expandedSection && (
        <div className="space-y-4">
          <div className="grid lg:grid-cols-12 gap-4">
            {/* Active Polls spotlight */}
            <div className="lg:col-span-6 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0d1a] to-[#0a1218] p-5 min-h-[300px]">
              <div className="pointer-events-none absolute -top-20 -left-20 w-64 h-64 rounded-full blur-[110px]" style={{ background: 'rgba(0,229,255,0.16)' }} />
              <div className="relative flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.22), rgba(138,43,226,0.16))', boxShadow: '0 0 18px -4px rgba(0,229,255,0.55)' }}>
                    <Vote className="w-4 h-4 text-cyan-300" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Team pulse</p>
                    <h3 className="text-white text-xl font-light">Live <span className="italic font-serif text-cyan-300">polls</span></h3>
                  </div>
                </div>
                {canManage && (
                  <button onClick={() => setShowPoll(true)}
                    className="group relative inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-medium text-black overflow-hidden transition-all hover:-translate-y-0.5"
                    style={{ background: 'linear-gradient(135deg, #00E5FF 0%, #8A2BE2 100%)', boxShadow: '0 10px 24px -8px rgba(0,229,255,0.55)' }}>
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    <Plus className="w-3 h-3" /> New poll
                  </button>
                )}
              </div>
              <div className="relative space-y-3 max-h-[400px] overflow-y-auto pr-1 jewel-scroll">
                {polls.filter(p => (p.status || 'active') === 'active').length === 0 ? (
                  <div className="text-center py-10 text-white/40 text-sm"><Vote className="w-10 h-10 mx-auto mb-2 opacity-25" />No polls yet — start the conversation.</div>
                ) : polls.filter(p => (p.status || 'active') === 'active').map(p => {
                  const totalVotes = p.total_votes || p.options?.reduce((s, o) => s + (o.votes || 0), 0) || 0;
                  const hasVoted = p.user_voted || p.voted_by?.includes(currentUser?.username);
                  return (
                    <div key={p.id} className="p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
                      <p className="text-white text-sm font-medium mb-3">{p.question}</p>
                      <div className="space-y-1.5">
                        {p.options?.map((o, i) => {
                          const pct = o.percentage ?? (totalVotes > 0 ? Math.round(((o.votes || 0) / totalVotes) * 100) : 0);
                          return (
                            <button key={i} onClick={() => !hasVoted && vote(p.id, i)} disabled={hasVoted}
                              className={`relative w-full text-left p-2.5 rounded-lg overflow-hidden text-xs transition-all ${hasVoted ? 'cursor-default' : 'hover:bg-white/[0.04]'}`}
                              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                              {hasVoted && <div className="absolute inset-y-0 left-0 rounded-lg opacity-80" style={{ width: `${pct}%`, background: 'linear-gradient(90deg, rgba(0,229,255,0.2), rgba(138,43,226,0.14))' }} />}
                              <div className="relative flex justify-between items-center">
                                <span className="text-white/85">{o.text || o}</span>
                                {hasVoted && <span className="text-cyan-300 font-semibold tabular-nums">{pct}%</span>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-white/30 text-[10px] mt-2">{totalVotes} votes · by {p.created_by_name}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Announcements */}
            <div className="lg:col-span-3 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#12100a] to-[#0a0d1a] p-5">
              <div className="pointer-events-none absolute -top-20 -right-20 w-56 h-56 rounded-full blur-[110px]" style={{ background: 'rgba(249,115,22,0.16)' }} />
              <div className="relative flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(249,115,22,0.24), rgba(244,63,94,0.14))', boxShadow: '0 0 18px -4px rgba(249,115,22,0.55)' }}>
                    <Megaphone className="w-4 h-4 text-orange-300" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Broadcasts</p>
                    <h3 className="text-white text-lg font-light">Announcements</h3>
                  </div>
                </div>
                <button onClick={() => setExpandedSection('announcements')} data-testid="expand-announcements-btn"
                  className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-all">
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="relative space-y-1.5 max-h-[280px] overflow-y-auto pr-1 jewel-scroll">
                {announcements.length === 0 ? (
                  <div className="text-center py-6 text-white/40 text-xs"><Megaphone className="w-8 h-8 mx-auto mb-1.5 opacity-25" />Quiet on the wire</div>
                ) : announcements.slice(0, 6).map(a => (
                  <div key={a.id} className="p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                    <div className="flex items-center gap-2 mb-1">
                      {a.priority === 'urgent' && <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse shadow-[0_0_8px_rgba(244,63,94,0.9)]" />}
                      <p className="text-white/90 text-xs font-medium truncate flex-1">{a.title}</p>
                    </div>
                    <p className="text-white/50 text-[11px] line-clamp-2">{a.content}</p>
                    <p className="text-white/30 text-[9px] mt-1">{a.author} · {a.created_at && new Date(a.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Idea drop / feedback */}
            <div className="lg:col-span-3 relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a1416] to-[#0a0d1a] p-5">
              <div className="pointer-events-none absolute -bottom-20 -left-20 w-56 h-56 rounded-full blur-[110px]" style={{ background: 'rgba(20,184,166,0.14)' }} />
              <div className="relative flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(20,184,166,0.22), rgba(6,182,212,0.16))', boxShadow: '0 0 18px -4px rgba(20,184,166,0.55)' }}>
                    <Lightbulb className="w-4 h-4 text-teal-300" strokeWidth={1.8} />
                  </div>
                  <div>
                    <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Voice</p>
                    <h3 className="text-white text-lg font-light">Idea drop</h3>
                  </div>
                </div>
                <button onClick={() => setExpandedSection('feedback')} data-testid="expand-feedback-btn"
                  className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-all">
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="relative space-y-1.5 max-h-[280px] overflow-y-auto pr-1 jewel-scroll">
                {feedbackList.length === 0 ? (
                  <div className="text-center py-6 text-white/40 text-xs"><Lightbulb className="w-8 h-8 mx-auto mb-1.5 opacity-25" />No ideas yet</div>
                ) : feedbackList.slice(0, 6).map(f => (
                  <div key={f.id} className="p-2.5 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                    <p className="text-white/90 text-xs truncate">{f.title || f.message?.substring(0, 60)}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`text-[9px] tracking-wide px-1.5 py-0.5 rounded-md border ${f.category === 'bug' ? 'bg-rose-500/[0.10] border-rose-500/25 text-rose-200' : f.category === 'feature' ? 'bg-blue-500/[0.10] border-blue-500/25 text-blue-200' : 'bg-white/[0.03] border-white/[0.08] text-white/50'}`}>{f.category || 'general'}</span>
                      <span className="text-white/30 text-[9px]">{f.author || 'Anonymous'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {hubTab === 'engagement' && expandedSection === 'announcements' && <AnnouncementsPage getAuthHeaders={getAuthHeaders} currentUser={currentUser} onBack={() => setExpandedSection(null)} />}
      {hubTab === 'engagement' && expandedSection === 'feedback' && <FeedbackPage getAuthHeaders={getAuthHeaders} currentUser={currentUser} onBack={() => setExpandedSection(null)} />}
      {hubTab === 'engagement' && expandedSection === 'polls' && <PollsPage getAuthHeaders={getAuthHeaders} currentUser={currentUser} onBack={() => setExpandedSection(null)} />}

      {/* ═══ AWARDS TAB — SOTM Showcase + Points Ring + Badge Vault ═══ */}
      {hubTab === 'awards' && !expandedSection && (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* SOTM Showcase */}
          <div className="relative overflow-hidden rounded-[24px] border border-amber-400/25 p-6" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.10) 0%, rgba(15,10,25,0.98) 55%, rgba(139,92,246,0.08) 100%)' }}>
            <div className="pointer-events-none absolute -top-24 -right-16 w-64 h-64 rounded-full blur-[110px]" style={{ background: 'rgba(251,191,36,0.28)' }} />
            <div className="pointer-events-none absolute -bottom-16 -left-16 w-56 h-56 rounded-full blur-[110px]" style={{ background: 'rgba(139,92,246,0.16)' }} />
            <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(251,191,36,0.55), transparent)' }} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.35), rgba(249,115,22,0.20))', boxShadow: '0 0 22px -4px rgba(251,191,36,0.7)' }}>
                  <Crown className="w-4 h-4 text-amber-200" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.28em] uppercase text-amber-200/70">Monthly honour</p>
                  <h3 className="text-white text-xl font-light">Staff of the <span className="italic font-serif text-amber-300">Month</span></h3>
                </div>
              </div>
              {sotm.current?.username ? (
                <>
                  <div className="relative w-24 h-24 mx-auto mb-4">
                    <div className="absolute inset-0 rounded-full animate-pulse" style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.5) 0%, transparent 70%)', filter: 'blur(12px)' }} />
                    <div className="relative w-24 h-24 rounded-full flex items-center justify-center text-3xl font-light text-amber-950 border-2 border-amber-200/50" style={{ background: 'linear-gradient(135deg, #FCD34D 0%, #F97316 100%)', boxShadow: '0 20px 40px -10px rgba(251,191,36,0.6), inset 0 2px 0 rgba(255,255,255,0.3)' }}>
                      {(sotm.current.display_name || sotm.current.username).charAt(0).toUpperCase()}
                    </div>
                    <Crown className="absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 text-amber-300 drop-shadow-[0_0_10px_rgba(251,191,36,1)]" />
                  </div>
                  <p className="text-white text-center text-lg font-medium">{sotm.current.display_name || sotm.current.username}</p>
                  <p className="text-amber-200/70 text-center text-xs mt-1">{sotm.current.reason || 'Outstanding contribution to the team'}</p>
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="w-24 h-24 mx-auto rounded-full border-2 border-dashed border-white/15 flex items-center justify-center mb-3">
                    <Crown className="w-8 h-8 text-white/25" />
                  </div>
                  <p className="text-white/50 text-sm">Nominations open</p>
                  <p className="text-white/30 text-xs mt-1">Awaiting this month's champion</p>
                </div>
              )}
            </div>
          </div>

          {/* Points progress ring */}
          <div className="relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#12100a] via-[#0f0d15] to-[#0a0a14] p-6">
            <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full blur-[100px]" style={{ background: 'rgba(0,229,255,0.14)' }} />
            <div className="relative flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.22), rgba(138,43,226,0.18))', boxShadow: '0 0 18px -4px rgba(0,229,255,0.6)' }}>
                <Zap className="w-4 h-4 text-cyan-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Your XP</p>
                <h3 className="text-white text-xl font-light">Points</h3>
              </div>
            </div>
            <div className="relative flex flex-col items-center">
              <div className="relative w-36 h-36 mb-4">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                  <defs>
                    <linearGradient id="ptsRing" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#00E5FF" />
                      <stop offset="100%" stopColor="#8A2BE2" />
                    </linearGradient>
                  </defs>
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke="url(#ptsRing)" strokeWidth="6" strokeLinecap="round"
                    strokeDasharray={`${Math.min(100, ((gamPoints.points || 0) % 1000) / 10) * 2.64} 264`}
                    style={{ filter: 'drop-shadow(0 0 8px rgba(0,229,255,0.6))' }} />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-3xl font-light text-white tabular-nums leading-none">{gamPoints.points || 0}</p>
                  <p className="text-[9px] tracking-[0.22em] uppercase text-white/40 mt-1">points</p>
                </div>
              </div>
              <p className="text-white/50 text-xs mb-3">{1000 - ((gamPoints.points || 0) % 1000)} pts to next milestone</p>
              {gamPoints.history?.length > 0 && (
                <div className="w-full space-y-1 max-h-32 overflow-y-auto jewel-scroll pr-1">
                  {gamPoints.history.slice(0, 5).map((h, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-white/[0.02] border border-white/[0.05]">
                      <span className="text-white/60 text-[11px] truncate">{h.reason}</span>
                      <span className="text-cyan-300 text-[11px] font-semibold tabular-nums">+{h.points}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Badge Vault */}
          <div className="relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#100a1a] via-[#0d0a15] to-[#0a0a14] p-6">
            <div className="pointer-events-none absolute -top-16 -left-16 w-56 h-56 rounded-full blur-[110px]" style={{ background: 'rgba(139,92,246,0.18)' }} />
            <div className="relative flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.28), rgba(236,72,153,0.18))', boxShadow: '0 0 18px -4px rgba(139,92,246,0.6)' }}>
                  <Trophy className="w-4 h-4 text-purple-300" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">Achievement vault</p>
                  <h3 className="text-white text-xl font-light">Badges</h3>
                </div>
              </div>
              <button onClick={() => setExpandedSection('gamification')} className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-all">
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="relative">
              <div className="grid grid-cols-3 gap-2 mb-3">
                {gamBadges.all_badges?.slice(0, 9).map(b => {
                  const earned = gamBadges.earned?.includes(b.id || b.name);
                  return (
                    <div key={b.id || b.name} className={`aspect-square rounded-2xl flex flex-col items-center justify-center gap-1 p-2 text-center transition-all ${earned ? 'border border-purple-400/30 bg-gradient-to-br from-purple-500/15 to-pink-500/10 shadow-[0_0_18px_-6px_rgba(139,92,246,0.6)]' : 'border border-white/[0.06] bg-white/[0.02] opacity-40'}`}>
                      <span className="text-2xl">{b.icon || '🏅'}</span>
                      <p className={`text-[9px] leading-tight truncate w-full ${earned ? 'text-white/90 font-medium' : 'text-white/40'}`}>{b.name}</p>
                    </div>
                  );
                })}
                {(!gamBadges.all_badges || gamBadges.all_badges.length === 0) && <p className="col-span-3 text-white/30 text-xs text-center py-6">No badges yet — earn your first!</p>}
              </div>
              <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                <p className="text-[10px] tracking-[0.15em] uppercase text-white/50">Progress</p>
                <p className="text-purple-300 text-xs tabular-nums font-semibold">{badgesEarned}/{badgesTotal || '—'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {hubTab === 'awards' && expandedSection === 'gamification' && <GamificationPage getAuthHeaders={getAuthHeaders} currentUser={currentUser} onBack={() => setExpandedSection(null)} />}

      {/* ═══ GAMES TAB — Arcade Bento + Champions Corner ═══ */}
      {hubTab === 'games' && (
        <div className="space-y-4">
          {/* Champions Corner (NEW) */}
          <div className="relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#12100a] via-[#0d0a1c] to-[#0a0f1e] p-5">
            <div className="pointer-events-none absolute -top-24 -right-16 w-64 h-64 rounded-full blur-[110px]" style={{ background: 'rgba(251,191,36,0.18)' }} />
            <div className="pointer-events-none absolute -bottom-16 -left-16 w-56 h-56 rounded-full blur-[110px]" style={{ background: 'rgba(139,92,246,0.14)' }} />
            <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.28), rgba(139,92,246,0.18))', boxShadow: '0 0 22px -4px rgba(251,191,36,0.55)' }}>
                  <Gamepad2 className="w-5 h-5 text-amber-200" strokeWidth={1.6} />
                </div>
                <div>
                  <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">The arcade</p>
                  <h3 className="text-white text-lg font-light">Champions <span className="italic font-serif text-amber-300">corner</span></h3>
                </div>
              </div>
              {(() => {
                const wins = gameHistory.filter(g => g.i_won).length;
                const draws = gameHistory.filter(g => g.draw).length;
                const total = gameHistory.length;
                const bestScore = Math.max(0, ...Object.values(scores));
                return [
                  { label: 'Total wins', value: wins, hex: '#10B981' },
                  { label: 'Games played', value: total, hex: '#00E5FF' },
                  { label: 'Personal best', value: bestScore || '—', hex: '#F59E0B' },
                ].map((k, i) => (
                  <div key={i} className="relative p-3 rounded-xl bg-white/[0.02] border border-white/[0.05]">
                    <p className="text-[9px] tracking-[0.22em] uppercase text-white/40 mb-1">{k.label}</p>
                    <p className="text-2xl font-light tabular-nums leading-none" style={{ color: k.hex }}>{k.value}</p>
                  </div>
                ));
              })()}
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a0a14] to-[#0d0a1c] p-5">
            {/* Games Sub-tabs — jewel pills */}
            <div className="flex items-center gap-1 overflow-x-auto pb-1 jewel-scroll mb-5">
              {[
                { id: 'solo', label: 'Solo arcade', icon: Play, count: SOLO_GAMES.length, hex: '#8A2BE2' },
                { id: 'multiplayer', label: 'Multiplayer', icon: Users, count: pendingForMe.length || null, hex: '#00E5FF' },
                { id: 'active', label: 'Active duels', icon: Swords, count: activeGames.length || null, hex: '#F59E0B' },
                { id: 'history', label: 'History', icon: Trophy, count: null, hex: '#10B981' },
              ].map(t => {
                const active = gamesSubTab === t.id;
                return (
                  <button key={t.id} onClick={() => setGamesSubTab(t.id)} data-testid={`games-subtab-${t.id}`}
                    className={`group relative inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-medium tracking-wide transition-all whitespace-nowrap ${active ? 'text-white' : 'text-white/50 hover:text-white/85'}`}
                    style={active ? { background: `linear-gradient(135deg, ${t.hex}22, ${t.hex}08)`, border: `1px solid ${t.hex}40`, boxShadow: `0 0 22px -6px ${t.hex}` } : { border: '1px solid transparent' }}>
                    <t.icon className="w-3.5 h-3.5" style={active ? { color: t.hex } : {}} />
                    {t.label}
                    {t.count > 0 && <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold" style={{ background: active ? t.hex : 'rgba(244,63,94,0.85)', color: active ? '#000' : '#fff' }}>{t.count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Solo Games — luxury tile grid */}
            {gamesSubTab === 'solo' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="solo-games-grid">
                {SOLO_GAMES.map(g => (
                  <button key={g.id} onClick={() => openGame(g)} data-testid={`solo-game-${g.id}`}
                    className="group relative overflow-hidden flex flex-col items-center gap-2.5 p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.16] hover:-translate-y-0.5 transition-all">
                    <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `radial-gradient(circle at 50% 0%, ${scores[g.id] ? 'rgba(251,191,36,0.16)' : 'rgba(255,255,255,0.05)'}, transparent 70%)` }} />
                    <div className={`relative w-12 h-12 rounded-2xl bg-gradient-to-br ${g.gradient} flex items-center justify-center group-hover:scale-110 transition-transform`}
                      style={{ boxShadow: '0 12px 28px -8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.14)' }}>
                      <g.icon className="w-5 h-5 text-white" strokeWidth={1.6} />
                    </div>
                    <p className="relative text-white text-[11px] font-medium text-center leading-tight">{g.name}</p>
                    <p className="relative text-white/35 text-[9px] text-center line-clamp-1">{g.desc}</p>
                    {scores[g.id] && (
                      <span className="relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/[0.10] border border-amber-500/25 text-amber-200 text-[9px] font-semibold">
                        <Crown className="w-2.5 h-2.5" /> {scores[g.id]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Multiplayer */}
            {gamesSubTab === 'multiplayer' && (
              <div className="space-y-5">
                {pendingForMe.length > 0 && (
                  <div>
                    <h4 className="text-[9px] tracking-[0.28em] uppercase text-white/45 mb-2 flex items-center gap-2">
                      <span className="inline-block w-1 h-1 rounded-full bg-amber-400 animate-pulse shadow-[0_0_8px_rgba(251,191,36,0.9)]" /> Incoming challenges
                    </h4>
                    <div className="space-y-2">
                      {pendingForMe.map(inv => {
                        const gInfo = MULTIPLAYER_GAMES.find(g => g.id === inv.game_type) || {};
                        const GIcon = gInfo.icon || Swords;
                        return (
                          <div key={inv.game_id} className="relative overflow-hidden flex items-center gap-3 p-3 rounded-2xl bg-white/[0.02] border border-amber-400/25" data-testid={`invite-${inv.game_id}`}>
                            <span className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 to-orange-500" />
                            <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${gInfo.gradient || 'from-slate-500 to-slate-600'} flex items-center justify-center flex-shrink-0`}>
                              <GIcon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1"><p className="text-white text-sm"><span className="text-cyan-300 font-medium">{inv.challenger_name}</span> challenged you</p><p className="text-white/40 text-xs">{gInfo.name}</p></div>
                            <div className="flex gap-2">
                              <button onClick={() => respondToInvite(inv.game_id, 'accept')}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500/[0.12] border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/[0.20] text-[11px] font-medium transition-all">
                                <Check className="w-3 h-3" /> Accept
                              </button>
                              <button onClick={() => respondToInvite(inv.game_id, 'decline')}
                                className="w-7 h-7 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/45 hover:text-rose-300 hover:bg-rose-500/[0.10] transition-all">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div>
                  <h4 className="text-[9px] tracking-[0.28em] uppercase text-white/45 mb-2 flex items-center gap-2"><Swords className="w-3 h-3 text-purple-300" /> Challenge a colleague</h4>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {MULTIPLAYER_GAMES.map(g => (
                      <button key={g.id} onClick={() => { setSelectedMultiplayerGame(g); setShowInviteModal(true); setInviteTarget(''); }} data-testid={`mp-game-${g.id}`}
                        className="group relative overflow-hidden flex items-center gap-3 p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-purple-400/30 hover:-translate-y-0.5 transition-all">
                        <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'radial-gradient(circle at 20% 20%, rgba(139,92,246,0.14), transparent 60%)' }} />
                        <div className={`relative w-11 h-11 rounded-2xl bg-gradient-to-br ${g.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}
                          style={{ boxShadow: '0 10px 24px -8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.14)' }}>
                          <g.icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="relative flex-1 text-left"><p className="text-white text-sm font-medium">{g.name}</p><p className="text-white/40 text-[10px]">{g.desc}</p></div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Active duels */}
            {gamesSubTab === 'active' && (
              <div className="space-y-2">
                {activeGames.length === 0 ? (
                  <div className="text-center py-10 text-white/40"><Swords className="w-12 h-12 mx-auto mb-2 opacity-25" /><p className="text-sm">No active duels. Challenge someone!</p></div>
                ) : activeGames.map(g => {
                  const gInfo = MULTIPLAYER_GAMES.find(mg => mg.id === g.game_type) || {};
                  const GIcon = gInfo.icon || Swords;
                  const oppName = g.challenger_username === currentUser?.username ? g.opponent_name : g.challenger_name;
                  return (
                    <button key={g.game_id} onClick={() => openActiveGame(g.game_id)} data-testid={`active-game-${g.game_id}`}
                      className="w-full flex items-center gap-3 p-3 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-400/30 transition-all">
                      <div className={`w-11 h-11 rounded-2xl bg-gradient-to-br ${gInfo.gradient || 'from-slate-500 to-slate-600'} flex items-center justify-center flex-shrink-0`}>
                        <GIcon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 text-left"><p className="text-white text-sm font-medium">{gInfo.name}</p><p className="text-white/40 text-xs">vs {oppName}</p></div>
                      {g.is_my_turn
                        ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/[0.12] border border-emerald-500/30 text-emerald-200 text-[10px] font-medium animate-pulse">Your move</span>
                        : <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/[0.08] text-white/50 text-[10px]">Waiting</span>}
                    </button>
                  );
                })}
              </div>
            )}

            {/* History */}
            {gamesSubTab === 'history' && (
              <div className="space-y-2">
                {gameHistory.length === 0 ? (
                  <div className="text-center py-10 text-white/40"><Trophy className="w-12 h-12 mx-auto mb-2 opacity-25" /><p className="text-sm">No games played yet</p></div>
                ) : gameHistory.slice(0, 10).map(g => {
                  const gInfo = MULTIPLAYER_GAMES.find(mg => mg.id === g.game_type) || {};
                  return (
                    <div key={g.game_id} className="relative overflow-hidden flex items-center gap-3 p-3 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
                      <span className={`absolute left-0 top-0 bottom-0 w-1 ${g.i_won ? 'bg-gradient-to-b from-emerald-400 to-teal-500' : g.draw ? 'bg-gradient-to-b from-amber-400 to-orange-500' : 'bg-gradient-to-b from-rose-400 to-red-500'}`} />
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${g.i_won ? 'bg-emerald-500/[0.10] border border-emerald-500/25' : g.draw ? 'bg-amber-500/[0.10] border border-amber-500/25' : 'bg-rose-500/[0.10] border border-rose-500/25'}`}>
                        {g.i_won ? <Trophy className="w-4 h-4 text-emerald-300" /> : g.draw ? <Dices className="w-4 h-4 text-amber-300" /> : <X className="w-4 h-4 text-rose-300" />}
                      </div>
                      <div className="flex-1"><p className="text-white/90 text-sm">{gInfo.name || g.game_type}</p><p className="text-white/40 text-xs">{g.challenger_name} vs {g.opponent_name}</p></div>
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold tracking-wide uppercase ${g.i_won ? 'bg-emerald-500/[0.12] border border-emerald-500/30 text-emerald-200' : g.draw ? 'bg-amber-500/[0.12] border border-amber-500/30 text-amber-200' : 'bg-rose-500/[0.12] border border-rose-500/30 text-rose-200'}`}>{g.i_won ? 'Won' : g.draw ? 'Draw' : 'Lost'}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* HR Section (Management Only) */}
      {hubTab === 'engagement' && !expandedSection && canManage && (
        <div className="relative overflow-hidden rounded-[24px] border border-white/[0.06] bg-gradient-to-br from-[#0a1614] to-[#0a0d1a] p-5">
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full blur-[110px]" style={{ background: 'rgba(16,185,129,0.14)' }} />
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.22), rgba(20,184,166,0.14))', boxShadow: '0 0 18px -4px rgba(16,185,129,0.5)' }}>
                <ClipboardList className="w-4 h-4 text-emerald-300" strokeWidth={1.8} />
              </div>
              <div>
                <p className="text-[9px] tracking-[0.28em] uppercase text-white/40">People ops</p>
                <h3 className="text-white text-xl font-light">HR <span className="italic font-serif text-emerald-300">centre</span></h3>
              </div>
            </div>
              <button onClick={() => { setHrInitialTab('expenses'); setExpandedSection('hr'); }} data-testid="expand-hr-btn"
                className="group relative inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-medium text-white overflow-hidden border border-white/10 hover:border-emerald-400/40 transition-all">
                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                Open centre <ArrowRight className="w-3 h-3" />
              </button>
          </div>
          <div className="relative grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { id: 'expenses',  label: 'Expenses',  icon: '💰', hex: '#F59E0B', desc: 'Submit & review' },
              { id: 'incidents', label: 'Incidents', icon: '⚠️', hex: '#F43F5E', desc: 'Report & track' },
              { id: 'payslips',  label: 'Payslips',  icon: '📄', hex: '#00E5FF', desc: 'View payroll' },
              { id: 'training',  label: 'Training',  icon: '🎓', hex: '#8A2BE2', desc: 'Records & certs' },
              { id: 'reviews',   label: 'Reviews',   icon: '⭐', hex: '#10B981', desc: 'Performance' },
            ].map((item) => (
              <button key={item.id} onClick={() => { setHrInitialTab(item.id); setExpandedSection('hr'); }}
                data-testid={`hr-tile-${item.id}`}
                className="group relative overflow-hidden flex flex-col items-start gap-2 p-4 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.18] hover:-translate-y-0.5 transition-all text-left">
                <div className="pointer-events-none absolute -top-10 -right-10 w-24 h-24 rounded-full blur-[50px] opacity-0 group-hover:opacity-70 transition-opacity" style={{ background: item.hex }} />
                <span className="relative text-2xl">{item.icon}</span>
                <div className="relative">
                  <p className="text-white text-sm font-medium">{item.label}</p>
                  <p className="text-white/40 text-[10px]">{item.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
      {hubTab === 'engagement' && expandedSection === 'hr' && <HRPage getAuthHeaders={getAuthHeaders} currentUser={currentUser} initialTab={hrInitialTab} onBack={() => setExpandedSection(null)} />}

      {/* Game Modal — Jewel */}
      <JewelModal open={showGame} onOpenChange={setShowGame} icon={game?.icon || Gamepad2} title={game?.name || 'Game'} subtitle={game?.desc || 'Play & win'} accent="violet" size="md" testid="solo-game-modal"
        footer={<JewelButton variant="ghost" onClick={() => setShowGame(false)}>Close</JewelButton>}>
          <div className="mt-1">
            {game?.id === 'memory' && (
              <div>
                <div className="flex justify-between items-center mb-3 text-sm"><span className="text-white/50">Moves: <span className="text-white font-bold">{moves}</span></span><Button onClick={initMem} size="sm" variant="outline" className="border-slate-600 h-7"><RotateCcw className="w-3 h-3 mr-1" />Reset</Button></div>
                <div className="grid grid-cols-4 gap-2">
                  {memoryCards.map(c => (
                    <button key={c.id} onClick={() => clickMem(c)} className={`aspect-square rounded-lg text-2xl flex items-center justify-center transition-all ${c.matched ? 'bg-green-500/30 border border-green-500' : flipped.includes(c.id) ? 'bg-purple-500/30 border border-purple-500' : 'bg-white/[0.02] hover:bg-slate-700 border border-white/[0.08]'}`}>
                      {(flipped.includes(c.id) || c.matched) ? c.emoji : '?'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {game?.id === 'trivia' && triviaQ && (
              <div>
                <div className="flex justify-between items-center mb-3 text-sm"><span className="text-white/50">Q{triviaNum + 1}/{TRIVIA_QS.length}</span><Badge className="bg-amber-500/20 text-amber-400">Score: {triviaScore}</Badge></div>
                <p className="text-white mb-4 p-3 bg-white/[0.02] rounded-lg">{triviaQ.q}</p>
                <div className="space-y-2">{triviaQ.opts.map((o, i) => (<button key={i} onClick={() => ansTriv(i)} className="w-full p-3 bg-white/[0.02] hover:bg-slate-700 rounded-lg text-left text-white text-sm border border-white/[0.08]">{o}</button>))}</div>
              </div>
            )}
            {game?.id === 'trivia' && !triviaQ && (<div className="text-center py-6"><Trophy className="w-12 h-12 mx-auto text-amber-400 mb-2" /><p className="text-white font-bold">Complete!</p><p className="text-white/50 text-sm">Score: {triviaScore}/{TRIVIA_QS.length}</p><Button onClick={initTriv} size="sm" className="mt-3 bg-amber-500">Play Again</Button></div>)}
            {game?.id === 'wordle' && (
              <div>
                <div className="space-y-2 mb-4">
                  {wordleGuesses.map((g, i) => (<div key={i} className="flex gap-1 justify-center">{g.split('').map((l, j) => (<div key={j} className={`w-10 h-10 ${wordColor(l, j)} rounded flex items-center justify-center text-white font-bold`}>{l}</div>))}</div>))}
                  {wordleGuesses.length < 6 && wordleGuesses[wordleGuesses.length - 1] !== wordleWord && (<div className="flex gap-1 justify-center">{Array(5).fill('').map((_, j) => (<div key={j} className="w-10 h-10 bg-white/[0.02] border border-slate-600 rounded flex items-center justify-center text-white font-bold">{wordleInput[j] || ''}</div>))}</div>)}
                </div>
                {wordleGuesses[wordleGuesses.length - 1] !== wordleWord && wordleGuesses.length < 6 && (<div className="flex gap-2"><input type="text" value={wordleInput} onChange={e => setWordleInput(e.target.value.slice(0, 5).toUpperCase())} onKeyPress={e => e.key === 'Enter' && guessWord()} placeholder="5 letters" className="flex-1 bg-white/[0.02] border border-white/[0.08] rounded px-3 py-2 text-white text-sm uppercase" maxLength={5} /><Button onClick={guessWord} size="sm" className="bg-green-500">Guess</Button></div>)}
              </div>
            )}
            {game?.id === 'reaction' && (
              <div className="text-center">
                {!reactionWaiting && !reactionStart && !reactionResult && (<div><p className="text-white/50 text-sm mb-4">Click Start, then click when it turns green!</p><Button onClick={startReact} className="bg-yellow-500 text-black"><Play className="w-4 h-4 mr-1" />Start</Button></div>)}
                {reactionWaiting && (<button onClick={clickReact} className="w-full h-40 bg-red-500 rounded-xl flex items-center justify-center text-white text-xl font-bold">Wait...</button>)}
                {reactionStart && (<button onClick={clickReact} className="w-full h-40 bg-green-500 rounded-xl flex items-center justify-center text-white text-xl font-bold animate-pulse">CLICK!</button>)}
                {reactionResult && (<div><div className="w-full h-40 bg-white/[0.02] rounded-xl flex flex-col items-center justify-center"><Zap className="w-10 h-10 text-yellow-400 mb-2" /><p className="text-3xl font-bold text-white">{reactionResult}</p></div><Button onClick={startReact} className="mt-4 bg-yellow-500 text-black">Again</Button></div>)}
              </div>
            )}
            {game?.id === 'typing' && (
              <div>
                <div className="p-4 bg-white/[0.02] rounded-lg mb-4 text-sm">{typingText.split('').map((c, i) => (<span key={i} className={i < typingInput.length ? typingInput[i] === c ? 'text-green-400' : 'text-red-400' : 'text-white/50'}>{c}</span>))}</div>
                {typingWPM ? (<div className="text-center"><p className="text-4xl font-bold text-cyan-400">{typingWPM}</p><p className="text-white/50 text-sm">WPM</p><Button onClick={initType} size="sm" className="mt-3 bg-cyan-500">Again</Button></div>) : (<input type="text" value={typingInput} onChange={e => onType(e.target.value)} placeholder="Start typing..." className="w-full bg-white/[0.02] border border-white/[0.08] rounded px-3 py-2 text-white text-sm" autoFocus />)}
              </div>
            )}
            {game?.id === 'number-guess' && (
              <div>
                <p className="text-white/50 text-sm mb-4 text-center">Guess a number between 1 and 100</p>
                {numberWon ? (
                  <div className="text-center py-4">
                    <Trophy className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
                    <p className="text-white font-bold text-lg">Got it in {numberAttempts.length} tries!</p>
                    <p className="text-white/50 text-sm">The number was {numberTarget}</p>
                    <Button onClick={initNumberGuess} size="sm" className="mt-3 bg-emerald-500">Play Again</Button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-1 mb-4 max-h-40 overflow-y-auto">
                      {numberAttempts.map((a, i) => (
                        <div key={i} className={`flex items-center justify-between p-2 rounded-lg text-sm ${a.hint === 'higher' ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                          <span className="text-white font-medium">{a.guess}</span>
                          <span className={`text-xs ${a.hint === 'higher' ? 'text-blue-400' : 'text-red-400'}`}>Go {a.hint}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="number" value={numberGuess} onChange={e => setNumberGuess(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && guessNumber()} min={1} max={100}
                        placeholder="Your guess" className="flex-1 bg-white/[0.02] border border-white/[0.08] rounded px-3 py-2 text-white text-sm" />
                      <Button onClick={guessNumber} size="sm" className="bg-rose-500">Guess</Button>
                    </div>
                  </>
                )}
              </div>
            )}
            {game?.id === 'emoji-quiz' && emojiQuiz && (
              <div>
                <div className="flex justify-between items-center mb-3 text-sm">
                  <span className="text-white/50">Q{emojiNum + 1}/{EMOJI_QUIZ_DATA.length}</span>
                  <Badge className="bg-violet-500/20 text-violet-400">Score: {emojiScore}</Badge>
                </div>
                <div className="bg-white/[0.02] rounded-xl p-6 text-center mb-4">
                  <p className="text-4xl mb-2">{emojiQuiz.emojis}</p>
                  <p className="text-white/40 text-xs">Hint: {emojiQuiz.hint}</p>
                </div>
                <div className="flex gap-2">
                  <input type="text" value={emojiAnswer} onChange={e => setEmojiAnswer(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && checkEmoji()}
                    placeholder="Type your answer..." className="flex-1 bg-white/[0.02] border border-white/[0.08] rounded px-3 py-2 text-white text-sm" />
                  <Button onClick={checkEmoji} size="sm" className="bg-violet-500">Check</Button>
                </div>
              </div>
            )}
            {game?.id === 'emoji-quiz' && !emojiQuiz && emojiNum > 0 && (
              <div className="text-center py-6">
                <Trophy className="w-12 h-12 mx-auto text-violet-400 mb-2" />
                <p className="text-white font-bold">Complete!</p>
                <p className="text-white/50 text-sm">Score: {emojiScore}/{EMOJI_QUIZ_DATA.length}</p>
                <Button onClick={initEmojiQuiz} size="sm" className="mt-3 bg-violet-500">Play Again</Button>
              </div>
            )}
            {game?.id === 'color-match' && (
              <div className="text-center">
                {colorTarget ? (
                  <>
                    <div className="flex justify-between items-center mb-3 text-sm">
                      <span className="text-white/50">Round {colorRound + 1}/{colorTotalRounds}</span>
                      <Badge className="bg-teal-500/20 text-teal-400">Score: {colorScore}</Badge>
                    </div>
                    <p className="text-white/50 text-sm mb-2">Tap the color that matches:</p>
                    <p className="text-2xl font-bold mb-4" style={{ color: COLOR_HEX[COLOR_NAMES[Math.floor(Math.random() * COLOR_NAMES.length)]] }}>{colorTarget}</p>
                    <div className="grid grid-cols-2 gap-3">
                      {colorOptions.map((c, i) => (
                        <button key={i} onClick={() => pickColor(c)}
                          className="h-16 rounded-xl border-2 border-white/10 hover:border-white/30 transition-all font-bold text-white"
                          style={{ backgroundColor: COLOR_HEX[c] }}>
                          {c}
                        </button>
                      ))}
                    </div>
                  </>
                ) : colorRound > 0 ? (
                  <div className="py-6">
                    <Trophy className="w-12 h-12 mx-auto text-teal-400 mb-2" />
                    <p className="text-white font-bold">Score: {colorScore}/{colorTotalRounds}</p>
                    <Button onClick={initColorMatch} size="sm" className="mt-3 bg-teal-500">Play Again</Button>
                  </div>
                ) : null}
              </div>
            )}

            {/* ═══ 2048 ═══ */}
            {game?.id === '2048' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-2">
                    <div className="px-3 py-1.5 rounded-lg bg-orange-500/[0.10] border border-orange-500/25 text-orange-200 text-[11px] font-semibold">Score <span className="tabular-nums ml-1">{g2048Score}</span></div>
                    <div className="px-3 py-1.5 rounded-lg bg-amber-500/[0.10] border border-amber-500/25 text-amber-200 text-[11px] font-semibold">Best <span className="tabular-nums ml-1">{scores['2048'] || 0}</span></div>
                  </div>
                  <button onClick={init2048} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium text-white bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.08] transition-all">
                    <RotateCcw className="w-3 h-3" /> New game
                  </button>
                </div>
                <div className="p-2 rounded-2xl bg-black/40 border border-white/[0.06] mx-auto" style={{ width: 'fit-content' }}>
                  {g2048.map((row, r) => (
                    <div key={r} className="flex">
                      {row.map((v, c) => {
                        const tileClass = v === 0 ? 'bg-white/[0.03] border-white/[0.05] text-transparent'
                          : v <= 4 ? 'bg-gradient-to-br from-amber-200 to-amber-300 text-amber-950 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]'
                          : v <= 16 ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-white shadow-[0_0_12px_-2px_rgba(249,115,22,0.6)]'
                          : v <= 64 ? 'bg-gradient-to-br from-rose-500 to-orange-500 text-white shadow-[0_0_14px_-2px_rgba(244,63,94,0.6)]'
                          : v <= 256 ? 'bg-gradient-to-br from-fuchsia-500 to-rose-500 text-white shadow-[0_0_16px_-2px_rgba(217,70,239,0.6)]'
                          : v <= 1024 ? 'bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-[0_0_18px_-2px_rgba(139,92,246,0.7)]'
                          : 'bg-gradient-to-br from-cyan-400 to-violet-500 text-white shadow-[0_0_24px_-2px_rgba(0,229,255,0.9)]';
                        const font = v >= 1024 ? 'text-lg' : v >= 128 ? 'text-xl' : 'text-2xl';
                        return (
                          <div key={c} className={`w-14 h-14 m-1 rounded-xl border flex items-center justify-center font-bold ${font} ${tileClass} transition-all`}>
                            {v > 0 ? v : '.'}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                {/* On-screen arrows for touch */}
                <div className="mt-3 grid grid-cols-3 gap-1.5 w-40 mx-auto">
                  <div></div>
                  <button onClick={() => g2048Move('up')} disabled={g2048Over} className="h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/80 hover:bg-white/[0.10] disabled:opacity-40">▲</button>
                  <div></div>
                  <button onClick={() => g2048Move('left')} disabled={g2048Over} className="h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/80 hover:bg-white/[0.10] disabled:opacity-40">◀</button>
                  <button onClick={() => g2048Move('down')} disabled={g2048Over} className="h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/80 hover:bg-white/[0.10] disabled:opacity-40">▼</button>
                  <button onClick={() => g2048Move('right')} disabled={g2048Over} className="h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/80 hover:bg-white/[0.10] disabled:opacity-40">▶</button>
                </div>
                <p className="text-center text-white/40 text-[10px] mt-2">Arrow keys or on-screen buttons · merge to <span className="text-amber-300">2048</span>{g2048Won && ' — reached!'}</p>
                {g2048Over && <div className="mt-3 p-3 rounded-xl bg-rose-500/[0.08] border border-rose-500/25 text-center"><p className="text-rose-200 text-sm font-medium">Game over</p><p className="text-white/60 text-xs">Final score: {g2048Score}</p></div>}
              </div>
            )}

            {/* ═══ Coin Streak ═══ */}
            {game?.id === 'coin-streak' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-2">
                    <div className="px-3 py-1.5 rounded-lg bg-yellow-500/[0.10] border border-yellow-500/25 text-yellow-200 text-[11px] font-semibold">🔥 Streak <span className="tabular-nums ml-1">{coinStreak}</span></div>
                    <div className="px-3 py-1.5 rounded-lg bg-amber-500/[0.10] border border-amber-500/25 text-amber-200 text-[11px] font-semibold">Best <span className="tabular-nums ml-1">{Math.max(coinBest, scores['coin-streak'] || 0)}</span></div>
                  </div>
                  <button onClick={initCoinStreak} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-medium text-white bg-white/[0.03] border border-white/[0.08] hover:bg-white/[0.08] transition-all">
                    <RotateCcw className="w-3 h-3" /> Reset
                  </button>
                </div>
                {/* Coin visual */}
                <div className="relative flex items-center justify-center py-6">
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-40 h-40 rounded-full blur-[70px]" style={{ background: coinFace === 'H' ? 'rgba(251,191,36,0.35)' : coinFace === 'T' ? 'rgba(148,163,184,0.30)' : 'rgba(255,255,255,0.05)' }} />
                  </div>
                  <div className={`relative w-28 h-28 rounded-full flex items-center justify-center border-4 ${coinFlipping ? 'animate-spin' : ''}`}
                    style={{
                      background: coinFace === 'T'
                        ? 'linear-gradient(135deg, #E2E8F0 0%, #94A3B8 100%)'
                        : 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                      borderColor: coinFace === 'T' ? '#CBD5E1' : '#FBBF24',
                      boxShadow: `0 20px 40px -10px ${coinFace === 'T' ? 'rgba(148,163,184,0.4)' : 'rgba(251,191,36,0.6)'}, inset 0 2px 0 rgba(255,255,255,0.4)`,
                    }}>
                    <span className={`text-4xl font-bold ${coinFace === 'T' ? 'text-slate-700' : 'text-amber-950'}`}>{coinFace || '?'}</span>
                  </div>
                </div>
                {/* Guess buttons */}
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button onClick={() => flipCoin('H')} disabled={coinFlipping} data-testid="coin-guess-h"
                    className="group relative overflow-hidden py-4 rounded-2xl text-black font-semibold text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0"
                    style={{ background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)', boxShadow: '0 12px 30px -8px rgba(251,191,36,0.55)' }}>
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    Heads
                  </button>
                  <button onClick={() => flipCoin('T')} disabled={coinFlipping} data-testid="coin-guess-t"
                    className="group relative overflow-hidden py-4 rounded-2xl text-slate-900 font-semibold text-sm transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0"
                    style={{ background: 'linear-gradient(135deg, #E2E8F0 0%, #94A3B8 100%)', boxShadow: '0 12px 30px -8px rgba(148,163,184,0.45)' }}>
                    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                    Tails
                  </button>
                </div>
                {/* History */}
                {coinHistory.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[9px] tracking-[0.28em] uppercase text-white/40 mb-1.5">Last flips</p>
                    <div className="flex gap-1.5">
                      {coinHistory.map((f, i) => (
                        <span key={i} className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${f === 'H' ? 'bg-amber-400/25 border border-amber-400/40 text-amber-200' : 'bg-slate-400/20 border border-slate-400/40 text-slate-200'}`}>{f}</span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-center text-white/40 text-[10px] mt-3">Pick correctly to grow your streak · one wrong call and it resets</p>
              </div>
            )}
          </div>
        </JewelModal>

      {/* Invite Modal — Jewel */}
      <JewelModal
        open={showInviteModal}
        onOpenChange={setShowInviteModal}
        icon={selectedMultiplayerGame?.icon || Send}
        title="Challenge Someone"
        subtitle={selectedMultiplayerGame ? `Pick a colleague for ${selectedMultiplayerGame.name}` : "Send a challenge"}
        accent="violet"
        size="md"
        testid="invite-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowInviteModal(false)}>Cancel</JewelButton>
          <JewelButton accent="violet" onClick={sendGameInvite} disabled={!inviteTarget} icon={Send}>Send challenge</JewelButton>
        </>}
      >
        <div className="space-y-3">
          <p className="text-white/45 text-xs">They'll get an invite in their Staff Chat DMs.</p>
          <div className="max-h-72 overflow-y-auto space-y-1 bg-white/[0.02] border border-white/[0.06] rounded-xl p-2 jewel-scroll">
            {staffList.map(s => {
              const active = inviteTarget === s.username;
              return (
                <button key={s.username} onClick={() => setInviteTarget(s.username)}
                  data-testid={`invite-user-${s.username}`}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-xl transition-all text-left border ${
                    active ? 'bg-gradient-to-r from-violet-500/15 to-pink-500/10 border-violet-400/30' : 'border-transparent hover:bg-white/[0.03] hover:border-white/[0.06]'
                  }`}>
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500/25 to-pink-500/25 border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-sm font-semibold">{(s.display_name || s.username).charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm truncate ${active ? 'text-white' : 'text-white/85'}`}>{s.display_name || s.username}</p>
                    <p className="text-white/40 text-[10px]">@{s.username}</p>
                  </div>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-violet-400' : 'border border-white/15'}`}>
                    {active && <Check className="w-2.5 h-2.5 text-[#05050A]" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </JewelModal>

      {/* Multiplayer Game Modal — Jewel */}
      <JewelModal open={showMultiplayerGame} onOpenChange={setShowMultiplayerGame}
        icon={selectedMultiplayerGame?.icon || Swords}
        title={selectedMultiplayerGame?.name || 'Duel'}
        subtitle={selectedMultiplayerGame?.desc || 'Multiplayer showdown'}
        accent="violet" size="md" testid="multiplayer-game-modal"
        footer={<JewelButton variant="ghost" onClick={() => setShowMultiplayerGame(false)}>Close</JewelButton>}>
        <div className="mt-1">
            {/* RPS */}
            {selectedMultiplayerGame?.id === 'rps' && mpGameState && (
              <div className="text-center">
                {mpGameState.result ? (
                  <div className="py-4">
                    <p className="text-3xl mb-3">
                      {mpGameState.result.draw ? '🤝' : mpGameState.result.winner === currentUser?.username ? '🎉' : '😔'}
                    </p>
                    <p className="text-white font-bold text-lg mb-2">
                      {mpGameState.result.draw ? 'Draw!' : mpGameState.result.winner === currentUser?.username ? 'You Won!' : 'You Lost!'}
                    </p>
                    {mpGameState.result.moves && (
                      <p className="text-white/50 text-sm">
                        Moves: {Object.entries(mpGameState.result.moves).map(([u, m]) => `${u}: ${m}`).join(' vs ')}
                      </p>
                    )}
                    <Button onClick={() => setShowMultiplayerGame(false)} size="sm" className="mt-4 bg-slate-700">Close</Button>
                  </div>
                ) : mpGameState.my_move ? (
                  <div className="py-8">
                    <p className="text-white/50 text-sm mb-2">You picked:</p>
                    <p className="text-4xl mb-4">{mpGameState.my_move === 'rock' ? '🪨' : mpGameState.my_move === 'paper' ? '📄' : '✂️'}</p>
                    <p className="text-white font-medium">Waiting for opponent...</p>
                    <Button onClick={() => openActiveGame(activeMultiplayerGame)} size="sm" variant="outline" className="mt-4 border-slate-600">
                      <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-white/50 text-sm mb-6 text-center">Choose your move!</p>
                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { id: 'rock', emoji: '🪨', label: 'Rock' },
                        { id: 'paper', emoji: '📄', label: 'Paper' },
                        { id: 'scissors', emoji: '✂️', label: 'Scissors' },
                      ].map(m => (
                        <button key={m.id} onClick={() => { setRpsChoice(m.id); makeMove(m.id); }}
                          data-testid={`rps-${m.id}`}
                          className="flex flex-col items-center gap-2 p-4 bg-white/[0.02] hover:bg-slate-700 rounded-xl border border-white/[0.08] hover:border-purple-500/40 transition-all">
                          <span className="text-4xl">{m.emoji}</span>
                          <span className="text-white text-xs font-medium">{m.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Trivia Battle */}
            {selectedMultiplayerGame?.id === 'trivia-battle' && mpGameState && (
              <div>
                {mpGameState.result ? (
                  <div className="text-center py-4">
                    <Trophy className="w-12 h-12 mx-auto text-amber-400 mb-2" />
                    <p className="text-white font-bold text-lg mb-1">
                      {mpGameState.result.draw ? 'Draw!' : mpGameState.result.winner === currentUser?.username ? 'You Won!' : 'You Lost!'}
                    </p>
                    {mpGameState.result.scores && (
                      <p className="text-white/50 text-sm">
                        {Object.entries(mpGameState.result.scores).map(([u, s]) => `${u}: ${s}`).join(' - ')}
                      </p>
                    )}
                    <Button onClick={() => setShowMultiplayerGame(false)} size="sm" className="mt-4 bg-slate-700">Close</Button>
                  </div>
                ) : mpGameState.question ? (
                  <div>
                    <div className="flex justify-between items-center mb-3 text-sm">
                      <span className="text-white/50">Q{mpGameState.current_question + 1}/{mpGameState.total_questions}</span>
                      <Badge className="bg-amber-500/20 text-amber-400">Score: {mpGameState.my_score}</Badge>
                    </div>
                    <p className="text-white mb-4 p-3 bg-white/[0.02] rounded-lg text-sm">{mpGameState.question.q}</p>
                    <div className="space-y-2">
                      {mpGameState.question.opts.map((o, i) => (
                        <button key={i} onClick={() => makeMove(String(i))}
                          className="w-full p-3 bg-white/[0.02] hover:bg-slate-700 rounded-lg text-left text-white text-sm border border-white/[0.08] transition-all">
                          {o}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-white font-medium mb-2">You've answered all questions!</p>
                    <p className="text-white/50 text-sm">Score: {mpGameState.my_score}</p>
                    <p className="text-white/40 text-xs mt-2">Waiting for opponent to finish...</p>
                    <Button onClick={() => openActiveGame(activeMultiplayerGame)} size="sm" variant="outline" className="mt-4 border-slate-600">
                      <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Word Duel */}
            {selectedMultiplayerGame?.id === 'word-duel' && mpGameState && (
              <div>
                {mpGameState.result ? (
                  <div className="text-center py-4">
                    <Trophy className="w-12 h-12 mx-auto text-emerald-400 mb-2" />
                    <p className="text-white font-bold text-lg mb-1">
                      {mpGameState.result.draw ? 'Draw!' : mpGameState.result.winner === currentUser?.username ? 'You Won!' : 'You Lost!'}
                    </p>
                    <p className="text-white/50 text-sm">The word was: <span className="text-white font-bold">{mpGameState.word}</span></p>
                    <Button onClick={() => setShowMultiplayerGame(false)} size="sm" className="mt-4 bg-slate-700">Close</Button>
                  </div>
                ) : mpGameState.finished ? (
                  <div className="text-center py-8">
                    <p className="text-white font-medium mb-2">
                      {mpGameState.my_result?.won ? `You guessed it in ${mpGameState.my_result.attempts} tries!` : 'You ran out of guesses'}
                    </p>
                    <p className="text-white/40 text-xs mt-2">Waiting for opponent...</p>
                    <Button onClick={() => openActiveGame(activeMultiplayerGame)} size="sm" variant="outline" className="mt-4 border-slate-600">
                      <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                    </Button>
                  </div>
                ) : (
                  <div>
                    <p className="text-white/50 text-sm mb-4 text-center">Guess the {mpGameState.word_length}-letter word in 6 tries!</p>
                    <div className="space-y-2 mb-4">
                      {(mpGameState.my_guesses || []).map((g, i) => (
                        <div key={i} className="flex gap-1 justify-center">
                          {g.split('').map((l, j) => {
                            const word = mpGameState.word || '';
                            const color = word[j] === l ? 'bg-green-500' : word.includes(l) ? 'bg-yellow-500' : 'bg-slate-600';
                            return <div key={j} className={`w-10 h-10 ${color} rounded flex items-center justify-center text-white font-bold text-sm`}>{l}</div>;
                          })}
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input type="text" value={wordDuelInput} onChange={e => setWordDuelInput(e.target.value.slice(0, 5).toUpperCase())}
                        onKeyPress={e => e.key === 'Enter' && wordDuelInput.length === 5 && makeMove(wordDuelInput)}
                        placeholder="5 letters" className="flex-1 bg-white/[0.02] border border-white/[0.08] rounded px-3 py-2 text-white text-sm uppercase" maxLength={5} />
                      <Button onClick={() => wordDuelInput.length === 5 && makeMove(wordDuelInput)} size="sm" className="bg-emerald-500" disabled={wordDuelInput.length !== 5}>Guess</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
      </JewelModal>

      {/* Kudos Modal — Jewel */}
      <JewelModal open={showKudos} onOpenChange={setShowKudos} icon={Award} title="Give Kudos" subtitle="Celebrate a teammate's brilliance" accent="rose" size="md" testid="kudos-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowKudos(false)}>Cancel</JewelButton>
          <JewelButton accent="rose" onClick={sendKudos} icon={Send} disabled={!kudosForm.recipient_username || !kudosForm.message.trim()}>Send kudos</JewelButton>
        </>}>
        <div className="space-y-4">
          <JewelField label="Recipient" required>
            <JewelSelect value={kudosForm.recipient_username} onChange={e => setKudosForm({ ...kudosForm, recipient_username: e.target.value })}>
              <option value="">Choose a teammate…</option>
              {staffList.map(s => <option key={s.username} value={s.username}>{s.display_name || s.username}</option>)}
            </JewelSelect>
          </JewelField>
          <JewelField label="Category">
            <div className="grid grid-cols-5 gap-2">
              {Object.entries(KUDOS_CATEGORIES).map(([k, c]) => {
                const active = kudosForm.category === k;
                return (
                  <button key={k} type="button" onClick={() => setKudosForm({ ...kudosForm, category: k })}
                    className={`group relative overflow-hidden p-2.5 rounded-xl border transition-all ${active ? 'border-white/25' : 'border-white/[0.06] hover:border-white/[0.15]'}`}
                    style={active ? { background: `linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))`, boxShadow: `0 0 18px -4px rgba(244,63,94,0.35)` } : { background: 'rgba(255,255,255,0.02)' }}>
                    <div className={`w-8 h-8 mx-auto rounded-xl bg-gradient-to-br ${c.gradient} flex items-center justify-center mb-1 ${active ? 'scale-105' : 'opacity-70 group-hover:opacity-100'} transition-all`}>
                      <c.icon className="w-4 h-4 text-white" strokeWidth={1.8} />
                    </div>
                    <p className={`text-[9px] tracking-wide text-center ${active ? 'text-white' : 'text-white/50'}`}>{c.label}</p>
                  </button>
                );
              })}
            </div>
          </JewelField>
          <JewelField label="Message">
            <JewelTextarea rows={3} value={kudosForm.message} onChange={e => setKudosForm({ ...kudosForm, message: e.target.value })} placeholder="What did they do brilliantly?" />
          </JewelField>
        </div>
      </JewelModal>

      {/* Poll Modal — Jewel */}
      <JewelModal open={showPoll} onOpenChange={setShowPoll} icon={Vote} title="Create Poll" subtitle="Ask the team a question" accent="cyan" size="md" testid="poll-modal"
        footer={<>
          <JewelButton variant="ghost" onClick={() => setShowPoll(false)}>Cancel</JewelButton>
          <JewelButton accent="cyan" onClick={createPoll} icon={Vote} disabled={!pollForm.question.trim() || pollForm.options.filter(o => o.trim()).length < 2}>Create poll</JewelButton>
        </>}>
        <div className="space-y-4">
          <JewelField label="Question" required>
            <JewelInput value={pollForm.question} onChange={e => setPollForm({ ...pollForm, question: e.target.value })} placeholder="Ask something…" />
          </JewelField>
          <JewelField label="Options" required>
            <div className="space-y-2">
              {pollForm.options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-lg bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/50 text-[10px] font-semibold tabular-nums">{i + 1}</span>
                  <JewelInput className="flex-1" value={o} onChange={e => { const n = [...pollForm.options]; n[i] = e.target.value; setPollForm({ ...pollForm, options: n }); }} placeholder={`Option ${i + 1}`} />
                  {pollForm.options.length > 2 && (
                    <button type="button" onClick={() => setPollForm({ ...pollForm, options: pollForm.options.filter((_, x) => x !== i) })}
                      className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.08] flex items-center justify-center text-white/40 hover:text-rose-300 hover:bg-rose-500/[0.10] transition-all">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {pollForm.options.length < 6 && (
                <button type="button" onClick={() => setPollForm({ ...pollForm, options: [...pollForm.options, ''] })}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/[0.08] border border-cyan-500/25 text-cyan-200 hover:bg-cyan-500/[0.14] text-[11px] font-medium transition-all">
                  <Plus className="w-3 h-3" /> Add option
                </button>
              )}
            </div>
          </JewelField>
        </div>
      </JewelModal>

      {/* Approvals Modal — Jewel */}
      <JewelModal open={showApprovals} onOpenChange={setShowApprovals} icon={AlertCircle} title="Pending review" subtitle="Approve or reject submissions" accent="amber" size="lg" testid="approvals-modal"
        footer={<JewelButton variant="ghost" onClick={() => setShowApprovals(false)}>Close</JewelButton>}>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto jewel-scroll pr-1">
          {pendingKudos.length === 0 && pendingPolls.length === 0 ? (
            <div className="text-center py-10">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/[0.10] border border-emerald-500/25 mb-3"><Check className="w-6 h-6 text-emerald-300" strokeWidth={1.6} /></div>
              <p className="text-white/70">All caught up</p>
              <p className="text-white/35 text-xs mt-1">No pending items right now.</p>
            </div>
          ) : (<>
            {pendingKudos.map(k => {
              const cat = KUDOS_CATEGORIES[k.category] || KUDOS_CATEGORIES.teamwork;
              return (
                <div key={k.id} className="relative overflow-hidden p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
                  <span className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${cat.gradient}`} />
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${cat.gradient} flex items-center justify-center flex-shrink-0`}><cat.icon className="w-4 h-4 text-white" /></div>
                    <div className="flex-1 min-w-0">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold tracking-[0.14em] uppercase border" style={{ borderColor: 'rgba(244,63,94,0.30)', background: 'rgba(244,63,94,0.10)', color: '#fda4af' }}>Kudos · {cat.label}</span>
                      <p className="text-white text-sm mt-1.5"><span className="font-medium">{k.sender_name}</span><span className="text-white/30 mx-1.5">→</span><span className="font-medium text-cyan-300">{k.recipient_name}</span></p>
                      <p className="text-white/60 text-xs mt-1 italic">"{k.message}"</p>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => approve('kudos', k.id, true)} className="w-8 h-8 rounded-full bg-emerald-500/[0.12] border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/[0.22] flex items-center justify-center transition-all"><Check className="w-4 h-4" /></button>
                      <button onClick={() => approve('kudos', k.id, false)} className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.08] text-white/40 hover:text-rose-300 hover:bg-rose-500/[0.14] flex items-center justify-center transition-all"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              );
            })}
            {pendingPolls.map(p => (
              <div key={p.id} className="relative overflow-hidden p-4 rounded-2xl bg-white/[0.02] border border-white/[0.05]">
                <span className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-400 to-violet-500" />
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/25 to-violet-500/25 border border-white/[0.08] flex items-center justify-center flex-shrink-0"><Vote className="w-4 h-4 text-cyan-300" /></div>
                  <div className="flex-1 min-w-0">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold tracking-[0.14em] uppercase border" style={{ borderColor: 'rgba(0,229,255,0.30)', background: 'rgba(0,229,255,0.10)', color: '#67e8f9' }}>Poll</span>
                    <p className="text-white text-sm mt-1.5 font-medium">{p.question}</p>
                    <p className="text-white/40 text-xs mt-0.5">by {p.created_by_name}</p>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => approve('poll', p.id, true)} className="w-8 h-8 rounded-full bg-emerald-500/[0.12] border border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/[0.22] flex items-center justify-center transition-all"><Check className="w-4 h-4" /></button>
                    <button onClick={() => approve('poll', p.id, false)} className="w-8 h-8 rounded-full bg-white/[0.03] border border-white/[0.08] text-white/40 hover:text-rose-300 hover:bg-rose-500/[0.14] flex items-center justify-center transition-all"><X className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </>)}
        </div>
      </JewelModal>
    </div>
  );
};

export default StaffHubPageRevamped;
